import { runInAction } from 'mobx'
import { isMobile, isTablet } from 'react-device-detect'
import ReactGA from 'react-ga4'

import { IQuery, ISubscription, LocationType } from '~/client/graph'
import {
  BasemapsByProjectIdDocument,
  ListenBasemapsByProjectIdDocument,
} from '~/client/graph/operations/generated/Basemaps.generated'
import {
  GetClosuresListDocument,
  ListenToClosuresDocument,
} from '~/client/graph/operations/generated/Closures.generated'
import {
  CompaniesByProjectIdDocument,
  CompanyTypeTagsDocument,
  ListenCompaniesByProjectIdDocument,
  ListenCompanyTypeTagDocument,
} from '~/client/graph/operations/generated/Companies.generated'
import {
  ConfigurationByNameDocument,
  ProjectConfigurationByNameDocument,
} from '~/client/graph/operations/generated/Configurations.generated'
import {
  GetThreadListDocument,
  ListenThreadDocument,
} from '~/client/graph/operations/generated/ContentObjects.generated'
import { GetDeliveryCustomFiltersByProjectDocument } from '~/client/graph/operations/generated/CustomDeliveryFilters.generated'
import { GetUploadLogCustomFiltersByProjectDocument } from '~/client/graph/operations/generated/CustomUploadLogFilters.generated'
import { GetWorkflowCustomFiltersByProjectDocument } from '~/client/graph/operations/generated/CustomWorkflowsFilter.generated'
import { GetDefaultPermitTypesDocument } from '~/client/graph/operations/generated/DefaultPermitType.generated'
import { DefaultTeamsDocument } from '~/client/graph/operations/generated/DefaultTeams.generated'
import { ListenDeliveriesByProjectIdDocument } from '~/client/graph/operations/generated/Deliveries.generated'
import {
  GetDeliveryAssignmentListDocument,
  ListenToDeliveryAssignmentsDocument,
} from '~/client/graph/operations/generated/DeliveryAssignments.generated'
import {
  GetDeliveryConfigurationsDocument,
  ListenToDeliveryConfigurationsDocument,
} from '~/client/graph/operations/generated/DeliveryConfigurations.generated'
import {
  GetDeliveryFieldsConfigurationsDocument,
  ListenToDeliveryFieldsConfigurationsDocument,
} from '~/client/graph/operations/generated/DeliveryFieldsConfigurations.generated'
import {
  GetDeliveryFollowingListDocument,
  ListenToDeliveryFollowingDocument,
} from '~/client/graph/operations/generated/DeliveryFollowing.generated'
import {
  GetFormCategoriesDocument,
  ListenFormCategoriesByProjectIdDocument,
} from '~/client/graph/operations/generated/FormCategories.generated'
import {
  GlobeViewsByProjectIdDocument,
  ListenGlobeViewItemsDataByProjectIdDocument,
  ListenGlobeViewsByProjectIdDocument,
} from '~/client/graph/operations/generated/GlobeViews.generated'
import {
  ListenLocationsByProjectIdDocument,
  ListenToWorkflowLocationRestrictionDocument,
  LocationsByProjectIdDocument,
} from '~/client/graph/operations/generated/LocationType.generated'
import {
  GetLogisticsConfigurationsDocument,
  ListenToLogisticsConfigurationsDocument,
} from '~/client/graph/operations/generated/LogisticsConfigurations.generated'
import {
  GetMaterialConfigurationDocument,
  ListenToMaterialConfigurationDocument,
} from '~/client/graph/operations/generated/MaterialConfiguration.generated'
import {
  GetMaterialCategoryListDocument,
  GetMaterialListDocument,
  ListenToMaterialCategoryDocument,
  ListenToMaterialDocument,
} from '~/client/graph/operations/generated/Materials.generated'
import {
  ListenMessagesByProjectIdDocument,
  MessagesByProjectIdDocument,
} from '~/client/graph/operations/generated/Messages.generated'
import {
  ListenNotificationsByProjectIdDocument,
  NotificationsByProjectIdDocument,
  UnreadNotificationsCountDocument,
} from '~/client/graph/operations/generated/Notifications.generated'
import {
  ListenOperationRuleByProjectIdDocument,
  OperationRulesByProjectIdDocument,
} from '~/client/graph/operations/generated/OperationRules.generated'
import {
  ListenPermitTypesByProjectIdDocument,
  PermitTypeListDocument,
} from '~/client/graph/operations/generated/PermitTypes.generated'
import {
  ListenPhotosByProjectIdDocument,
  PhotosByProjectIdDocument,
} from '~/client/graph/operations/generated/Photos.generated'
import {
  GetAvailableProjectsDocument,
  ListenToProjectDocument,
} from '~/client/graph/operations/generated/Project.generated'
import {
  GetProjectAddressDocument,
  ListenToProjectAddressDocument,
} from '~/client/graph/operations/generated/ProjectAddress.generated'
import {
  GetProjectColoringOptionsDocument,
  ListenToProjectColoringOptionsDocument,
} from '~/client/graph/operations/generated/ProjectColoringOptions.generated'
import {
  GetProjectHistoryDocument,
  ListenToProjectHistoryDocument,
} from '~/client/graph/operations/generated/ProjectHistory.generated'
import {
  GetProjectMaterialOptionsDocument,
  ListenToProjectMaterialOptionsDocument,
} from '~/client/graph/operations/generated/ProjectMaterialOptions.generated'
import {
  GetProjectTypeOptionsDocument,
  ListenToProjectTypeOptionsDocument,
} from '~/client/graph/operations/generated/ProjectTypeOptions.generated'
import {
  GetRecurringDeliveriesSettingsByIdDocument,
  ListenToRecurringDeliveriesSettingsDocument,
} from '~/client/graph/operations/generated/RecurringDeliveriesSettings.generated'
import {
  GetRecurringWorkflowSettingsByIdDocument,
  ListenToRecurringWorkflowSettingsDocument,
} from '~/client/graph/operations/generated/RecurringWorkflowSettings.generated'
import {
  GetRemoteTilesetsDocument,
  ListenRemoteTilesetsByProjectIdDocument,
} from '~/client/graph/operations/generated/RemoteTilesets.generated'
import { GetReportTemplateDocument } from '~/client/graph/operations/generated/Reports.generated'
import {
  ListenToResourcePropertiesDocument,
  ListenToResourceTemplatesDocument,
} from '~/client/graph/operations/generated/Resources.generated'
import {
  ListenScanHistoriesByProjectIdDocument,
  ScanHistoriesByProjectIdDocument,
} from '~/client/graph/operations/generated/ScanHistory.generated'
import {
  ListenScannersByProjectIdDocument,
  ScannersByProjectIdDocument,
} from '~/client/graph/operations/generated/Scanners.generated'
import {
  GetSharedDeliveriesDocument,
  ListenToSharedDeliveriesDocument,
} from '~/client/graph/operations/generated/SharedDelivery.generated'
import {
  GetSharedSitePermitsDocument,
  ListenToSharedSitePermitsDocument,
} from '~/client/graph/operations/generated/SharedSitePermits.generated'
import {
  ListenSitemapItemsByProjectIdDocument,
  SitemapItemsByProjectIdDocument,
} from '~/client/graph/operations/generated/SitemapItems.generated'
import {
  ListenSitemapItemDataByProjectIdDocument,
  ListenSitemapsByProjectIdDocument,
  SitemapsByProjectIdDocument,
} from '~/client/graph/operations/generated/Sitemaps.generated'
import {
  GetSitePermitAssignmentListDocument,
  ListenToSitePermitAssignmentsDocument,
} from '~/client/graph/operations/generated/SitePermitAssignment.generated'
import {
  GetSitePermitFollowingListDocument,
  ListenToSitePermitFollowingDocument,
} from '~/client/graph/operations/generated/SitePermitFollow.generated'
import {
  GetSitePermitInspectionChangeListDocument,
  GetSitePermitInspectionListDocument,
  ListenToSitePermitInspectionChangeDocument,
  ListenToSitePermitInspectionDocument,
} from '~/client/graph/operations/generated/SitePermitInspections.generated'
import {
  GetSitePermitStatusChangeListDocument,
  ListenToSitePermitStatusChangeDocument,
} from '~/client/graph/operations/generated/SitePermitStatusChanges.generated'
import {
  ListenTeamsByProjectIdDocument,
  TeamsByProjectIdDocument,
} from '~/client/graph/operations/generated/Teams.generated'
import {
  GetMapboxTilesetsDocument,
  ListenTilesetsByProjectIdDocument,
} from '~/client/graph/operations/generated/Tilesets.generated'
import {
  ListenTradesByProjectIdDocument,
  TradesByProjectIdDocument,
} from '~/client/graph/operations/generated/Trades.generated'
import {
  GetUploadLogConfigurationDocument,
  ListenToUploadLogConfigurationDocument,
} from '~/client/graph/operations/generated/UploadLogConfiguration.generated'
import {
  ListenUserProjectsByProjectIdDocument,
  UserProjectForActiveUserDocument,
  UserProjectsByProjectIdDocument,
} from '~/client/graph/operations/generated/UserProjects.generated'
import {
  ListenUserRolesByProjectIdDocument,
  UserRolesByProjectIdDocument,
} from '~/client/graph/operations/generated/UserRoles.generated'
import {
  GetCurrentUserDocument,
  GetDeletedProjectMembersDocument,
  GetProjectMembersDocument,
  ListenToUsersByProjectDocument,
} from '~/client/graph/operations/generated/Users.generated'
import { UserTagsDocument } from '~/client/graph/operations/generated/UserTags.generated'
import {
  GetWeatherForecastsRangeDocument,
  ListenWeatherForecastsByProjectIdDocument,
} from '~/client/graph/operations/generated/WeatherForecast.generated'
import {
  ListenMarkupsByProjectIdDocument,
  MarkupsByProjectIdDocument,
} from '~/client/graph/operations/generated/WorkflowMarkups.generated'
import {
  GetWorkflowsConfigurationsDocument,
  ListenToWorkflowsConfigurationsDocument,
} from '~/client/graph/operations/generated/WorkflowsConfigurations.generated'
import {
  GetZoneMapThresholdsDocument,
  ListenToZoneMapThresholdsDocument,
} from '~/client/graph/operations/generated/ZoneMapThresholds.generated'
import Config, { isRemote } from '~/client/src/shared/Config'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import ConfigurationName from '~/client/src/shared/enums/ConfigurationName'
import InfoCode from '~/client/src/shared/enums/InfoCode'
import Project from '~/client/src/shared/models/Project'
import User from '~/client/src/shared/models/User'
import { LocationKind } from '~/client/src/shared/resources/domain/locationKind'
import InitialState from '~/client/src/shared/stores/InitialState'
import RootStore from '~/client/src/shared/stores/Root.store'
import IAssociationDto from '~/client/src/shared/types/IAssociationDto'
import ILocationDto from '~/client/src/shared/types/ILocationDto'
import {
  isPermitType,
  isWorkflowMessageType,
} from '~/client/src/shared/types/NotificationTypes'

import { disposeChatSession } from '../../chat2/web/composition'
import commonRoutes, {
  buildDeviceRedirectUrl,
  getInitProjectCodeFromPath,
  getJHPRedirectUrl,
  getRedirect,
  isProjectBasedPath,
  isSelfHandledAuthEntryPath,
  UrlParamKey,
} from '../../constants/commonRoutes'
import { flaggedFeaturesList } from '../../enums/FlaggedFeatures'
import { flaggedFormCategoriesList } from '../../enums/FlaggedFormCategories'
import IUserPreferences from '../../interfaces/IUserPreferences'
import UserProject from '../../models/UserProject'
import { listToMap } from '../../utils/converters'
import getIntegrationsConfigName from '../../utils/getIntegrationsConfigName'
import {
  isLocalHostName,
  isLoginBundle,
  isTenantBootInterceptPath,
  redirectToLogin,
} from '../../utils/loginPortal'
import { isNativePortalHost } from '../../utils/nativeBridge'
import {
  safeGetItem,
  safeGetJson,
  safeRemoveItem,
  safeSetItem,
} from '../../utils/safeStorage'
import { EffectMap, IEvent } from './BaseEvents.store'
import registerCommonChangeEvents from './commonChangeEvents'
import * as e from './eventConstants'
import EventsStore from './Events.store'
import {
  handleCompleteRequest,
  handleReportError,
  handleRequestError,
} from './handlers'
import registerLiveDataResyncEvents from './liveDataResyncEvents'
import {
  locationRoutingKey,
  OTHER_LOCATIONS_ROUTING_KEY,
} from './locationRouting'

interface IRegisterCommonEventsOptions {
  handleAuthEntry?: (
    state: InitialState,
    urlParams: URLSearchParams,
  ) => void | EffectMap
}

export default function (
  eventsStore: EventsStore,
  rootStore: RootStore,
  options: IRegisterCommonEventsOptions = {},
) {
  let locationLoadGeneration = 0
  let workflowRestrictionGeneration = 0

  // Landing after a URL-token login (invite key, handoff code): route, then run
  // the unified auth init that resolves the tenant and dispatches INIT_APP.
  const completeTokenLogin = (state: InitialState) => {
    rootStore.common.displayAuthSuccessRoute()
    state.didInitialize = true
    rootStore.auth.initAuthUser()
  }

  // Every store the twelve kind arms and the catch-all arm below write to. Listed rather
  // than derived because the stores have no common registry - they are independently
  // declared fields on the root store. The catch-all is one of them: a location moves
  // between it and a kind store when its kind is first written or changed to a custom one.
  const legacyKindStores = (
    root,
  ): { byId: Map<string, unknown>; clearFromStorage(id: string): void }[] => [
    root.buildingsStore,
    root.zonesStore,
    root.gatesStore,
    root.routesStore,
    root.levelsStore,
    root.areasStore,
    root.logisticsObjectsStore,
    root.verticalObjectsStore,
    root.stagingsStore,
    root.interiorDoorsStore,
    root.interiorPathsStore,
    root.projectLocationsStore,
    root.otherLocationsStore,
  ]

  /**
   * Notes which kind-backed stores hold a location, and returns the cleanup to run once the
   * delta has been routed: it drops every entry the routing did not replace.
   *
   * The kind is an editable form field, so a location can change bucket at any time. The
   * routing switch only ADDS to the destination, and each store's receiveOne is a bare
   * `byId.set`, so without this the row lives in both its old and new bucket - visible as a
   * duplicate wherever those stores are concatenated. Nothing records what the previous
   * bucket was, so the destination is recognised after the fact: receiveOne swaps in a fresh
   * instance there, while a stale bucket still holds the one noted here.
   *
   * Not "evict everywhere, then route": the destination would delete and re-add the row,
   * which moves it to the end of the Map - and the stores' lists read that order, sorted by
   * name at load - so every edit would send a location to the bottom of its list.
   */
  const staleKindEntriesCleanup = (root, id: string) => {
    const held = legacyKindStores(root)
      .filter(Boolean)
      .map(store => ({ store, entry: store.byId.get(id) }))
      .filter(({ entry }) => entry !== undefined)

    return () =>
      held.forEach(({ store, entry }) => {
        if (store.byId.get(id) === entry) store.clearFromStorage(id)
      })
  }

  eventsStore.on(e.START_ALL, (state, onAuthStateChangedCallback) => {
    state.onAuthStateChangedCallback = onAuthStateChangedCallback

    return {
      dispatchN: [
        [e.REDIRECT_TO_PROPER_SITE_VERSION],
        [e.DETECT_USER_LOCATION_INFO],
      ],
    }
  })

  eventsStore.on(e.REDIRECT_TO_PROPER_SITE_VERSION, state => {
    if (!Config.REDIRECT_CONFIG) {
      return {
        dispatch: [e.REQUEST_ERROR, e.REDIRECT_TO_PROPER_SITE_VERSION],
      }
    }

    const urlParams = new URLSearchParams(location.search)

    state.initProjectCode = getInitProjectCodeFromPath()

    // Store original pathname for later auth flow decisions
    // (React Router may change URL before auth check completes)
    state.initPathname = location.pathname

    // Capture the full destination — path + query + hash — minus the
    // single-use auth tokens, so the post-login fly-over replays it verbatim
    // and never drops a URL component (STX1-582). Stripping only the known
    // tokens (rather than enumerating what to keep) means any future param
    // rides through for free; initPathname alone dropped the query.
    const deepLinkParams = new URLSearchParams(location.search)
    deepLinkParams.delete(UrlParamKey.StxToken)
    deepLinkParams.delete(UrlParamKey.InviteKey)
    deepLinkParams.delete(UrlParamKey.ForceInviteKey)
    deepLinkParams.delete(UrlParamKey.HandoffCode)
    const deepLinkSearch = deepLinkParams.toString()
    state.initDeepLink =
      location.pathname +
      (deepLinkSearch ? `?${deepLinkSearch}` : '') +
      location.hash

    const { redirectRequired, redirect, host } = getRedirect(
      Config.REDIRECT_CONFIG,
      isMobile,
      isTablet,
    )

    if (redirectRequired) {
      if (!redirect) {
        return {
          dispatch: [e.REQUEST_ERROR, e.REDIRECT_TO_PROPER_SITE_VERSION],
        }
      }

      return {
        cmd: {
          cmd: {
            execute: () => {
              location.assign(buildDeviceRedirectUrl(host))
              return Promise.resolve()
            },
          },
          onSuccess: [e.NO_EFFECT],
          onError: [e.NO_EFFECT],
        },
      }
    }

    // Tenant runtime should not host auth pages - redirect those entry paths
    // to centralized login before any tenant auth initialization begins.
    if (isTenantBootInterceptPath(state.initPathname)) {
      const didRedirect = redirectToLogin(
        {
          pathname: state.initPathname,
          search: location.search,
          hash: location.hash,
        },
        { ignoreTokenBypass: true },
      )

      if (didRedirect) {
        return {
          dispatch: [e.INIT_COMPLETE],
        }
      }
    }

    const authEntryEffect = options.handleAuthEntry?.(state, urlParams)
    if (authEntryEffect) {
      return authEntryEffect
    }

    // Self-handled auth entry — see isSelfHandledAuthEntryPath.
    if (isSelfHandledAuthEntryPath(state.initPathname)) {
      state.onAuthStateChangedCallback()
      return {
        dispatch: [e.INIT_COMPLETE],
      }
    }

    const inviteKey = urlParams.get(UrlParamKey.InviteKey)
    const handoffCode = urlParams.get(UrlParamKey.HandoffCode)
    // Legacy MN (<= 1.6.3) WebView token. No longer honoured (STX1-398), but
    // still stripped: it is a live bearer token.
    const stxToken = urlParams.get(UrlParamKey.StxToken)

    // Strip sensitive tokens from URL (browser history, Referer headers)
    // Preserve existing history state to avoid corrupting the router
    if (stxToken || inviteKey || handoffCode) {
      const url = new URL(window.location.href)
      url.searchParams.delete(UrlParamKey.StxToken)
      url.searchParams.delete(UrlParamKey.InviteKey)
      url.searchParams.delete(UrlParamKey.HandoffCode)
      url.searchParams.delete(UrlParamKey.ForceInviteKey)
      const normalizedPathname = url.pathname.replace(/^\/{2,}/, '/')
      window.history.replaceState(
        window.history.state,
        '',
        normalizedPathname + url.search + url.hash,
      )
    }

    return {
      dispatch: [
        e.INIT_AUTH_USER,
        inviteKey,
        urlParams.get(UrlParamKey.ForceInviteKey),
        handoffCode,
      ],
    }
  })

  eventsStore.on(
    e.INIT_AUTH_USER,
    (
      state,
      inviteKey?: string,
      _forceInviteKey?: string,
      handoffCode?: string,
    ) => {
      // If we have a pending token, handle it via INIT_AUTH_USER_2
      if (inviteKey || handoffCode) {
        return {
          dispatch: [e.INIT_AUTH_USER_2, inviteKey, handoffCode],
        }
      }

      // Unified auth init — works for all modes (no tenanted/tenantless split).
      // Checks localStorage session, resolves tenant, dispatches INIT_APP.
      // Falls back to INIT_AUTH_USER_2 if no session found.
      state.didInitialize = true
      rootStore.auth.initAuthUser()
    },
  )

  eventsStore.on(
    e.INIT_AUTH_USER_2,
    (state, inviteKey?: string, handoffCode?: string) => {
      state.loading.set(e.INIT_AUTH_USER, true)
      // The UI renders synchronously in the callback below: flag the exchange
      // first, or the portal route bounces to /login until it completes.
      if (handoffCode) {
        state.loading.set(e.LOGIN_WITH_HANDOFF_CODE, true)
      }
      state.onAuthStateChangedCallback?.()

      // JHP redirect for unauthenticated users (no pending auth tokens)
      if (!inviteKey && !handoffCode) {
        const jhpUrl = getJHPRedirectUrl(
          state.initPathname,
          state.initProjectCode,
        )
        if (jhpUrl) {
          window.location.href = jhpUrl
          return
        }
      }

      if (handoffCode) {
        return {
          dispatch: [e.LOGIN_WITH_HANDOFF_CODE, handoffCode],
        }
      }

      if (inviteKey) {
        return {
          dispatch: [e.LOGIN_WITH_INVITE_KEY, inviteKey],
        }
      }

      // No session, no tokens — probe for unauthorized (public) mode.
      // INIT_COMPLETE is NOT dispatched here in parallel: releasing the
      // render gate now would let the tenant shell mount the deep-link
      // route, which a guard rewrites to /projects — a flash of the empty
      // projects list before the login bounce, and the rewrite clobbers
      // the deep link (STX1-540). Instead TRY_UNAUTHORIZED_MODE_RESULT
      // releases the gate only when we are NOT doing a full-page redirect
      // to login. While the gate stays closed the loader stays up and
      // location.href keeps the original deep link, so the bounce carries
      // it verbatim as return_to.
      return {
        dispatch: [e.TRY_UNAUTHORIZED_MODE],
      }
    },
  )

  eventsStore.on(e.TRY_UNAUTHORIZED_MODE_RESULT, state => {
    // Public path (signup, shared delivery, …): render it — release gate.
    if (state.isUnauthorizedMode) {
      return { dispatch: [e.INIT_COMPLETE] }
    }

    const jhpUrl = getJHPRedirectUrl(state.initPathname, state.initProjectCode)
    if (jhpUrl) {
      // Full-page redirect to the JHP host: keep the gate closed (loader
      // stays up) through the navigation — no /projects flash.
      window.location.href = jhpUrl
      return
    }

    // Bounce to the login portal, preserving return_to: this is the
    // unauth bootstrap, the one flow that must carry the deep link
    // forward so the post-login fly-over can honor it (STX1-540).
    // displayLoginView() returns true when it triggers a full-page
    // redirect (tenant bundle) — keep the gate closed so the loader
    // carries through it. It returns false when no redirect happened
    // (login bundle in-app nav, or an invite/bypass token on the URL):
    // release the gate so the routed view renders.
    if (!rootStore.common.displayLoginView({ preserveReturnTo: true })) {
      return { dispatch: [e.INIT_COMPLETE] }
    }
  })

  eventsStore.on(
    e.LOGIN_WITH_EMAIL_AND_PASSWORD,
    (
      state,
      email: string,
      password: string,
      callbackSuccess,
      callbackError,
    ) => {
      state.loading.set(e.LOGIN_WITH_EMAIL_AND_PASSWORD, true)

      return {
        cmd: {
          cmd: {
            execute: () => rootStore.auth.login(email, password),
          },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.LOGIN_WITH_EMAIL_AND_PASSWORD,
            callbackSuccess,
          ],
          onError: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            e.LOGIN_WITH_EMAIL_AND_PASSWORD,
            callbackError,
          ],
        },
      }
    },
  )

  eventsStore.on(e.LOGIN_WITH_INVITE_KEY, (state, inviteKey: string) => {
    state.loading.set(e.LOGIN_WITH_INVITE_KEY, true)

    return {
      cmd: {
        cmd: {
          execute: () => rootStore.auth.loginWithInviteKey(inviteKey),
        },
        onSuccess: [
          e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
          e.LOGIN_WITH_INVITE_KEY,
          () => completeTokenLogin(state),
        ],
        onError: [e.LOGIN_WITH_INVITE_KEY_ERROR, inviteKey],
      },
    }
  })

  /**
   * Login with a single-use session handoff code (STX1-495). Same landing as an
   * invite key. The code is already stripped from the URL, so a reload never
   * re-exchanges it.
   */
  eventsStore.on(e.LOGIN_WITH_HANDOFF_CODE, (state, handoffCode: string) => {
    state.loading.set(e.LOGIN_WITH_HANDOFF_CODE, true)

    return {
      cmd: {
        cmd: {
          execute: () => rootStore.auth.loginWithHandoffCode(handoffCode),
        },
        onSuccess: [
          e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
          e.LOGIN_WITH_HANDOFF_CODE,
          () => completeTokenLogin(state),
        ],
        onError: [e.LOGIN_WITH_HANDOFF_CODE_ERROR],
      },
    }
  })

  /**
   * An unusable handoff code (used, expired, auth down) is not an error page:
   * boot normally, from this domain's stored session or else to login.
   */
  eventsStore.on(e.LOGIN_WITH_HANDOFF_CODE_ERROR, state => {
    state.loading.set(e.LOGIN_WITH_HANDOFF_CODE, false)
    // The native app's portal WebView outlives a sign-out in the app, so its
    // stored session can belong to another account: never fall back to it
    // there. With no session the page goes to /login, and the app hands over
    // a fresh code (STX1-1235). A single WebView, so no other tab is affected.
    if (isNativePortalHost()) {
      rootStore.struxHubAuthService.logout()
    }
    state.didInitialize = true
    rootStore.auth.initAuthUser()
  })

  eventsStore.on(
    e.LOGIN_WITH_INVITE_KEY_ERROR,
    (state, inviteKey: string, error: Error) => {
      state.loading.set(e.LOGIN_WITH_INVITE_KEY, false)
      const code = error?.message || ''
      // Invite keys are replayable credentials, so only an expired one (no
      // longer exchangeable; the info page needs it to resend the invite)
      // goes on the URL.
      const payload =
        code === InfoCode.EXPIRED_INVITE_TOKEN && inviteKey ? inviteKey : ''
      rootStore.common.displayInfoPage(code, payload)

      return {
        dispatch: [e.INIT_COMPLETE],
      }
    },
  )

  /**
   * State app launch sequence by getting currently authenticated user from db.
   * Keeping the events decoupled (not chain dispatching) and only reliant on
   * state makes them easy to reason about.
   */
  eventsStore.on(e.INIT_APP, state => {
    state.loading.set(e.INIT_AUTH_USER, false)
    state.loading.set(e.INIT_APP, true)

    const dispatchN: IEvent[] = [
      [
        e.GET_CONFIGURATION,
        ConfigurationName.DefaultMandatoryFields,
        e.DEFAULT_MANDATORY_FIELDS_CONFIGURATION_RECEIVED,
      ],
      [
        e.GET_CONFIGURATION,
        ConfigurationName.DefaultHiddenFields,
        e.DEFAULT_HIDDEN_FIELDS_CONFIGURATION_RECEIVED,
      ],
      [e.GET_DEFAULT_TEAMS],
      [e.LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS],
      [e.LOAD_USER_TAGS],
      [e.LOAD_PROJECTS],
    ]

    // Prevent crashes
    if (!isLoginBundle()) {
      dispatchN.unshift([e.GET_DEFAULT_WORKFLOW_TYPES])
    }

    return {
      flow: {
        startWith: [[e.GET_CURRENT_USER]],
        rules: [
          {
            when: 'on',
            event: e.GET_CURRENT_USER_SUCCESS,
            dispatchN,
          },
          {
            when: 'on',
            event: e.PROJECTS_RECEIVED,
            dispatch: [e.INIT_APP_2], // this chain will trigger GET_AUTH_USER_PROJECT in order to trigger INIT_APP_3 afterwards
          },
          {
            when: 'on',
            event: e.GET_AUTH_USER_PROJECT_SUCCESS,
            dispatch: [e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS],
          },
          {
            when: 'on',
            event: e.PROJECT_TYPE_OPTIONS_RECEIVED,
            dispatch: [e.INIT_APP_3],
            shouldTerminate: true,
          },
          {
            when: 'on',
            event: e.REQUEST_ERROR,
            dispatch: [e.INIT_ERROR],
            shouldTerminate: true,
          },
        ],
      },
    }
  })

  eventsStore.on(e.GET_AUTH_USER_PROJECT, state => {
    state.loading.set(e.GET_AUTH_USER_PROJECT, true)
    state.userActiveProjectSettings = null

    return {
      graphQuery: {
        query: UserProjectForActiveUserDocument,
        variables: {
          projectId: state.activeProject.id,
          userId: state.user.id,
        },
        onSuccess: [e.GET_AUTH_USER_PROJECT_SUCCESS],
        onError: [e.REQUEST_ERROR, e.GET_AUTH_USER_PROJECT],
      },
    }
  })

  eventsStore.on(
    e.GET_AUTH_USER_PROJECT_SUCCESS,
    (state, { userProject }: IQuery) => {
      state.loading.set(e.GET_AUTH_USER_PROJECT, false)
      state.userActiveProjectSettings = UserProject.fromDto(userProject)
    },
  )

  eventsStore.on(e.LOAD_PROJECTS, () => {
    return {
      graphQuery: {
        query: GetAvailableProjectsDocument,
        variables: {},
        onSuccess: [e.PROJECTS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_PROJECTS],
      },
    }
  })

  eventsStore.on(
    e.PROJECTS_RECEIVED,
    (_, { getAvailableProjects: receivedProjects }: IQuery) => {
      rootStore.projectsStore.receiveList(receivedProjects)
      rootStore.projectsStore.loadProjectAddresses()
    },
  )

  /**
   * Get users last viewed project (user.settings) or the users first project
   */
  eventsStore.on(e.INIT_APP_2, state => {
    state.loading.set(e.INIT_APP, false)
    state.loading.set(e.INIT_APP_2, true)

    if (shouldInterruptInitialization(rootStore)) {
      return { dispatch: [e.INIT_COMPLETE] }
    }

    const { getProjectByCode, lastViewedProjectCode } = rootStore.projectsStore

    // If initProjectCode was cleared (e.g., by triggering LOGOUT during unauthenticated
    // flow), try to recover it from initPathname which survives the logout.
    let effectiveInitProjectCode = state.initProjectCode
    if (!effectiveInitProjectCode && state.initPathname) {
      const pathnameProjectCode = getInitProjectCodeFromPath(state.initPathname)
      if (pathnameProjectCode) {
        effectiveInitProjectCode = pathnameProjectCode.toUpperCase()
      }
    }

    // Validate project code - getProjectByCode returns undefined if user has no access.
    const initProjectDto = getProjectByCode(effectiveInitProjectCode)
    const shouldFallbackToLastViewed = !effectiveInitProjectCode
    const lastViewedProjectDto = shouldFallbackToLastViewed
      ? getProjectByCode(lastViewedProjectCode)
      : undefined
    const nextActiveProjectDto = initProjectDto || lastViewedProjectDto

    // Always let user choose in centralized login runtime
    if (!isLoginBundle() && nextActiveProjectDto) {
      state.activeProject = Project.fromDto(nextActiveProjectDto)
    } else if (
      !isLoginBundle() &&
      effectiveInitProjectCode &&
      !initProjectDto
    ) {
      // User is trying to access a specific project they don't have access to.
      // Skip GET_AUTH_USER_PROJECT since there's no active project to query.
      return { dispatch: [e.INIT_COMPLETE] }
    } else {
      const { list: projectsList } = rootStore.projectsStore
      const projectsCount = projectsList.length

      if (!projectsCount) {
        // No accessible projects — let bootstrap complete so the /projects
        // route renders its inline empty state (with a logout affordance);
        // see shared/views/Projects/Projects.tsx.
        return { dispatch: [e.INIT_COMPLETE] }
      }

      // Many projects, user must pick one to load, do nothing
      if (projectsCount > 1) {
        return { dispatch: [e.INIT_APP_SELECT_PROJECT] }
      }

      // Exactly one project, load it if possible
      if (projectsCount === 1) {
        const [firstProjectDto] = projectsList

        if (firstProjectDto) {
          state.activeProject = Project.fromDto(firstProjectDto)
        }
      }
    }

    // Bootstrap landed the user on a non-project path (typically /projects,
    // the Redirector fallback when there's no selectedProjectCode in the
    // login-bundle handoff) but INIT_APP_2 just resolved an activeProject
    // — push '/' so the bundle's catch-all redirects into /project/<code>/
    // <viewToDisplay>. Without this the picker would render briefly with
    // the single project, the user would need to click, and ACTIVATE_PROJECT
    // would do exactly this redirect anyway. Only fires when activeProject
    // got set in this handler (not in the multi-project INIT_APP_SELECT_PROJECT
    // branch — that path returns early so we never reach this line).
    if (!isLoginBundle() && state.activeProject?.id && !isProjectBasedPath()) {
      rootStore.common.displayDefaultView()
    }

    return {
      dispatch: [e.GET_AUTH_USER_PROJECT],
    }
  })

  eventsStore.on(e.DETECT_USER_LOCATION_INFO, state => {
    const IPAPI_URL = 'https://ipapi.co/json/'

    const url = Config.IPAPI_KEY
      ? IPAPI_URL + `?key=${Config.IPAPI_KEY}`
      : IPAPI_URL

    fetch(url)
      .then(response => {
        response.json().then(jsonData => {
          if (jsonData.error) {
            const jsonString = JSON.stringify(jsonData)
            throw new Error(
              `Error interacting with the IPAPI service ${jsonString}`,
            )
          }

          runInAction(() => {
            state.countryCode = jsonData.country.toLowerCase()
            state.latitude = parseFloat(jsonData.latitude)
            state.longitude = parseFloat(jsonData.longitude)
          })
        })
      })
      .catch(error => console.warn(error))
  })

  /**
   * Something went wrong when trying to launch the app and we had to stop
   */
  eventsStore.on(e.INIT_ERROR, (state, reason: string) => {
    state.didInitialize = false

    console.warn('Application launch failure:')

    if (reason) {
      console.warn(reason)
    }

    console.log('Only limited routes will be available')
  })

  eventsStore.on(e.GET_PROJECT_MEMBERS, state => {
    state.loading.set(e.GET_PROJECT_MEMBERS, true)

    // Load both regular and deleted members in parallel
    Promise.all([
      rootStore.graphExecutorStore.query(GetProjectMembersDocument, {
        projectId: state.activeProject.id,
      }),
      rootStore.graphExecutorStore.query(GetDeletedProjectMembersDocument, {
        projectId: state.activeProject.id,
      }),
    ])
      .then(([membersResult, deletedMembersResult]) => {
        eventsStore.dispatch(e.GET_PROJECT_MEMBERS_SUCCESS, {
          users: membersResult.data?.users || { data: [] },
          deletedUsers: deletedMembersResult.data?.getDeletedUsers || [],
        })
      })
      .catch(error => {
        eventsStore.dispatch(e.REQUEST_ERROR, e.GET_PROJECT_MEMBERS, error)
      })
  })

  /**
   * Set the project from which data should be displayed
   */

  // TODO move this to user object?
  eventsStore.on(e.ACTIVATE_PROJECT, (_, projectId: string) => {
    const project = rootStore.projectsStore.getProjectById(projectId)

    return {
      dispatch: [e.ACTIVATE_PROJECT_SUCCESS, project],
    }
  })

  /**
   * Sync locally stored user prefs to state and optional object of prefs to merge in.
   * Preferences derived from state will override passed in prefs as they are the truth
   * no matter what anyone else says :)
   */
  eventsStore.on(e.UPDATE_USER_PREFERENCES, (state, prefsToUpdate = {}) => {
    if (!state.user) {
      return
    }

    const userPrefs: IUserPreferences = {
      lastViewedProject: '',
    }

    Object.assign(userPrefs, safeGetJson(state.user.id) || {})

    safeSetItem(
      state.user.id,
      JSON.stringify({
        ...userPrefs,
        ...prefsToUpdate,
        ...{
          lastViewedProject: state.activeProject.id,
        },
      }),
    )
  })

  eventsStore.on(e.LOAD_PROJECT_STATUSES, () => {
    const dispatchN = []

    rootStore.projectsStore.list.forEach(project => {
      if (rootStore.projectsStore.projectStatuses.has(project.id)) {
        return
      }

      dispatchN.push([e.GET_PROJECT_STATUS, project.id])
    })

    return {
      dispatchN,
    }
  })

  eventsStore.on(e.GET_PROJECT_STATUS, () => {
    // Temporarily disable until we can pull this from the api
    // return {
    //   db: {
    //     type: GET_BY_ID,
    //     collection: 'projects',
    //     id: projectFbId,
    //     onSuccess: [e.GET_PROJECT_ACTIVITIES],
    //     onError: [e.REQUEST_ERROR, e.GET_PROJECT_STATUS],
    //   },
    // }
  })

  eventsStore.on(e.LOGOUT, state => {
    state.loading.set(e.LOGOUT, true)

    return {
      cmd: {
        cmd: {
          execute: async () => {
            rootStore.tenantsStore.clearTenants()
            rootStore.auth.logout()

            // Clear the Apollo cache so the next user can't see this user's
            // data; keep the same client instance to preserve React-Apollo
            // hook subscriptions (replacing the client orphans them — same
            // issue we fixed in initAuthUser). Pre-auth operations work
            // through the same client; the auth callback simply returns
            // null once session is cleared.
            await state.graphClient?.clearStore()
          },
        },
        onSuccess: [e.LOGOUT_SUCCESS],
        onError: [e.REQUEST_ERROR, e.LOGOUT],
      },
    }
  })

  eventsStore.on(e.LOGOUT_SUCCESS, state => {
    eventsStore.terminateGraphSubscriptions()
    rootStore.chatService.disconnect()
    disposeChatSession()
    rootStore.struxTagHistoryStore.clear()

    // On desktop/mobile bundles redirectToLogin() triggers a full-page
    // navigation to the login portal — the new bundle boots with its
    // own fresh state, so clearing in-memory state here is unnecessary.
    // Worse: nulling `state.user` synchronously crashes observer
    // components still mounted at logout (e.g. desktop and mobile
    // Logistics views read `appState.user.id` without optional
    // chaining), and ErrorBoundary surfaces a "Something went wrong"
    // page before the navigation completes (STX1-497).
    // Logout ends the session: bounce with NO return_to (STX1-540) so the
    // next login lands context-free on the picker, never auto-routed back
    // to the tenant the user just left.
    //
    // Local-dev exception: the picked tenant's canonical host is the REMOTE
    // backend (e.g. dev.struxsite.com), not the dev server. Dropping
    // return_to here would send re-login to the remote host instead of the
    // LAN origin, so when logging out FROM a local app (not the login
    // bundle) we preserve return_to to return to the dev server. Prod and
    // remote keep the context-free behavior.
    const omitReturnTo =
      process.env.NODE_ENV === 'production' ||
      isLoginBundle() ||
      !isLocalHostName(location.hostname)
    if (redirectToLogin({ pathname: commonRoutes.LOGIN }, { omitReturnTo })) {
      return
    }

    // No full-page redirect happened — either we're already on the
    // login bundle (it owns the same SPA across login/logout) or the
    // URL carries an invite/bypass token. Navigate in-app and reset
    // the in-memory state. displayLoginView() is context-free by default,
    // so this drops any return_to still on the URL (e.g. the Redirector
    // failure UI dispatches LOGOUT at /redirector?return_to=…) — the next
    // login lands clean, never auto-routed back (STX1-540).
    rootStore.common.displayLoginView()

    state.loading.clear()
    state.user = null
    state.tenantUserSession = null
    // Session state, so it has to end with the session — this path keeps the
    // same bundle, so without resetting it the next sign-in inherits the
    // previous user's dismissal and is never asked (STX1-1057).
    state.isSavePasswordPromptOpen = false
    state.isSavePasswordPromptDismissed = false
    state.activeProject = Project.none
    state.userActiveProjectSettings = null
    state.initProjectCode = ''
    // Drop the captured deep link so a second in-SPA login can't replay a
    // stale destination (STX1-582).
    state.initDeepLink = ''
    state.resetResourceDisplayedOnGlobalCard()

    rootStore.projectsStore.clear()

    state.resetToDefaultDeliveryConfigurations()
    state.resetToDefaultLogisticsConfigurations()
    state.resetToDefaultWorkflowsConfigurations()
    rootStore.materialConfigurationStore.resetToDefault()
  })

  eventsStore.on(e.LISTEN_TO_NOTIFICATIONS_COUNT, state => {
    return {
      graphSubscription: {
        query: ListenNotificationsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.NOTIFICATION_COUNT_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_NOTIFICATIONS_COUNT],
      },
    }
  })

  let notificationLoadRetries = 0
  const MAX_NOTIFICATION_LOAD_RETRIES = 3

  eventsStore.on(e.LOAD_AND_LISTEN_TO_NOTIFICATIONS, state => {
    if (notificationLoadRetries >= MAX_NOTIFICATION_LOAD_RETRIES) {
      state.loading.set(e.LOAD_NOTIFICATIONS, false)
      notificationLoadRetries = 0
      return {
        dispatch: [e.LOAD_UNREAD_NOTIFICATIONS_COUNT],
      }
    }
    notificationLoadRetries++
    state.loading.set(e.LOAD_NOTIFICATIONS, true)
    return {
      graphQuery: {
        query: NotificationsByProjectIdDocument,
        variables: { projectId: state.activeProject.id },
        onSuccess: [e.NOTIFICATIONS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_NOTIFICATIONS],
      },
    }
  })

  eventsStore.on(
    e.NOTIFICATIONS_RECEIVED,
    (state, { notifications }: IQuery) => {
      notificationLoadRetries = 0
      const data = notifications?.data || []
      rootStore.notificationsStore.receiveList(data)
      const permitIds =
        rootStore.notificationsStore.getUniquePermitEntityIds(data)
      if (permitIds.length > 0) {
        rootStore.notificationsStore
          .fetchPermits(permitIds)
          .then(() =>
            runInAction(() => state.loading.set(e.LOAD_NOTIFICATIONS, false)),
          )
      } else {
        state.loading.set(e.LOAD_NOTIFICATIONS, false)
      }

      return {
        dispatch: [e.LOAD_UNREAD_NOTIFICATIONS_COUNT],
      }
    },
  )

  let countSeq = 0
  let countFailures = 0
  let countInFlight = false
  let countPending = false
  const MAX_COUNT_FAILURES = 3

  eventsStore.on(e.LOAD_UNREAD_NOTIFICATIONS_COUNT, state => {
    if (countInFlight) {
      countPending = true
      return
    }
    countInFlight = true
    const projectId = state.activeProject.id
    const seq = ++countSeq
    return {
      graphQuery: {
        query: UnreadNotificationsCountDocument,
        variables: { projectId },
        onSuccess: [e.UNREAD_NOTIFICATIONS_COUNT_RECEIVED, projectId, seq],
        onError: [e.UNREAD_NOTIFICATIONS_COUNT_RECEIVED, projectId, seq, null],
      },
    }
  })

  eventsStore.on(
    e.UNREAD_NOTIFICATIONS_COUNT_RECEIVED,
    (
      state,
      requestProjectId: string,
      requestSeq: number,
      queryResult: IQuery | null,
    ) => {
      countInFlight = false

      const isError = queryResult === null
      const hasPending = countPending
      countPending = false

      // Always flush a pending refresh — even if this response is stale or
      // failed.  A project switch while a request was in flight queues the
      // new project's refresh here; dropping it would leave the badge at 0.
      const pendingEffect: EffectMap | undefined = hasPending
        ? { dispatch: [e.LOAD_UNREAD_NOTIFICATIONS_COUNT] }
        : undefined

      if (isError) {
        countFailures++
        if (
          countFailures < MAX_COUNT_FAILURES &&
          requestProjectId === state.activeProject?.id
        ) {
          return { dispatch: [e.LOAD_UNREAD_NOTIFICATIONS_COUNT] }
        }
        return pendingEffect
      }

      countFailures = 0
      if (requestProjectId !== state.activeProject?.id) return pendingEffect
      if (requestSeq !== countSeq) return pendingEffect

      rootStore.notificationsStore.setUnreadNotificationsCount(
        queryResult.unreadNotificationsCount ?? 0,
      )
      return pendingEffect
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS, true)

    return {
      graphQuery: {
        query: CompanyTypeTagsDocument,
        onSuccess: [e.COMPANY_TYPE_TAGS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_COMPANY_TYPE_TAGS, () => {
    return {
      graphSubscription: {
        query: ListenCompanyTypeTagDocument,
        onData: [e.COMPANY_TYPE_TAGS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_COMPANY_TYPE_TAGS],
      },
    }
  })

  eventsStore.on(e.COMPANY_TYPE_TAGS_RECEIVED, (state, { companyTypeTags }) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_COMPANY_TYPE_TAGS, false)

    rootStore.companiesStore.receiveTypeTagsList(companyTypeTags?.data || [])

    // We are currently unable to change Company Type Tags. So there is no need to subscribe

    // return {
    //   dispatch: [e.LISTEN_TO_COMPANY_TYPE_TAGS],
    // }
  })

  eventsStore.on(e.LOAD_USER_TAGS, () => {
    return {
      graphQuery: {
        query: UserTagsDocument,
        onSuccess: [e.USER_TAGS_RECEIVED],
        // Reports and continues rather than raising REQUEST_ERROR, which the
        // INIT_APP flow turns into INIT_ERROR with shouldTerminate. Tags are a
        // display concern; a backend that cannot serve them must not stop the
        // app from starting. Same shape as the basemaps subscription below.
        onError: [e.REPORT_ERROR, e.USER_TAGS_RECEIVED],
      },
    }
  })

  eventsStore.on(e.USER_TAGS_RECEIVED, (_state, payload?) => {
    // Also reached from the error path, where the payload is the failure rather
    // than a query result — degrade to no tags instead of throwing.
    rootStore.tagsStore.receiveUserTags(payload?.userTags || [])
  })

  eventsStore.on(
    e.COMPANY_TYPE_TAGS_UPDATED,
    (_: InitialState, { companyTypeTag }: ISubscription) => {
      if (companyTypeTag) {
        rootStore.companiesStore.receiveOneTypeTag(
          companyTypeTag.id,
          companyTypeTag.item,
        )
      }
    },
  )

  eventsStore.on(e.NOTIFICATION_COUNT_UPDATED, (state, data) => {
    const items = Array.isArray(data) ? data : [data]
    const permitIds: string[] = []
    items.forEach(({ notification }) => {
      if (notification) {
        const model = rootStore.notificationsStore.receiveOne(
          notification.id,
          notification.item,
        )
        if (
          model &&
          (isPermitType(model.type) || isWorkflowMessageType(model.type)) &&
          model.entityId
        ) {
          permitIds.push(model.entityId)
        }
      }
    })
    if (permitIds.length > 0) {
      rootStore.notificationsStore.fetchPermits(permitIds)
    }

    return {
      dispatch: [e.LOAD_UNREAD_NOTIFICATIONS_COUNT],
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_THREADS, state => {
    return {
      graphQuery: {
        query: GetThreadListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.THREADS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_THREADS],
      },
    }
  })

  eventsStore.on(e.THREADS_RECEIVED, (_, { threads }: IQuery) => {
    rootStore.threadsStore.receiveList(threads ? threads.data : [])

    return {
      dispatch: [e.LISTEN_TO_THREAD],
    }
  })

  eventsStore.on(e.LISTEN_TO_THREAD, state => {
    return {
      graphSubscription: {
        query: ListenThreadDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.THREAD_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_THREAD],
      },
    }
  })

  eventsStore.on(
    e.THREAD_UPDATED,
    (_: InitialState, { thread }: ISubscription) => {
      if (thread) {
        rootStore.threadsStore.receiveOne(thread.id, thread.item)
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES, true)

    // Drop the previous project's types before (re)loading so a failed load
    // falls back to defaults instead of showing the last project's list.
    rootStore.permitTypesStore.clearProjectSpecificTypes()

    return {
      graphQuery: {
        query: PermitTypeListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.WORKFLOW_TYPES_LOAD_SUCCESS],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOW_TYPES_LOAD_SUCCESS,
    (state: InitialState, { permitTypes }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES, false)

      rootStore.workflowTypesStore.receiveProjectSpecificTypes(permitTypes.data)

      return {
        dispatch: [e.LISTEN_TO_WORKFLOW_TYPES],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_WORKFLOW_TYPES, state => {
    return {
      graphSubscription: {
        query: ListenPermitTypesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.WORKFLOW_TYPES_RECEIVED],
        listenMany: true,
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_WORKFLOW_TYPES],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOW_TYPES_RECEIVED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ permitType }) => {
        if (permitType) {
          rootStore.workflowTypesStore.receiveProjectSpecificType(
            permitType.id,
            permitType.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_MESSAGES, (state: InitialState) => {
    return {
      graphQuery: {
        query: MessagesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.MESSAGES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_MESSAGES],
      },
    }
  })

  eventsStore.on(e.MESSAGES_RECEIVED, (_, { messages }: IQuery) => {
    rootStore.messagesStore.receiveList(messages.data || [])

    return {
      dispatch: [e.LISTEN_TO_MESSAGES],
    }
  })

  eventsStore.on(e.LISTEN_TO_MESSAGES, state => {
    return {
      graphSubscription: {
        query: ListenMessagesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.MESSAGE_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_MESSAGES],
      },
    }
  })

  eventsStore.on(
    e.MESSAGE_UPDATED,
    (_: InitialState, { message }: ISubscription) => {
      if (!message) {
        return
      }

      rootStore.messagesStore.updateOne(message.id, message.item)
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PHOTOS, (state: InitialState) => {
    return {
      graphQuery: {
        query: PhotosByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PHOTOS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PHOTOS],
      },
    }
  })

  eventsStore.on(e.PHOTOS_RECEIVED, (_: InitialState, { photos }: IQuery) => {
    rootStore.photosStore.receiveList(photos.data || [])

    return {
      dispatch: [e.LISTEN_TO_PHOTOS],
    }
  })

  eventsStore.on(e.LISTEN_TO_PHOTOS, state => {
    return {
      graphSubscription: {
        query: ListenPhotosByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PHOTO_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PHOTOS],
      },
    }
  })

  eventsStore.on(
    e.PHOTO_UPDATED,
    (_: InitialState, { photo }: ISubscription) => {
      if (!photo) {
        return
      }

      rootStore.photosStore.updateOne(photo.id, photo.item)
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_ALL_DELIVERY_ATTRIBUTES, () => {
    rootStore.deliveryVehicleTypesStore.loadAndListenTo()
    rootStore.deliveryUnitsStore.loadAndListenTo()
  })

  eventsStore.on(
    e.LOAD_DELIVERY_ATTRIBUTES,
    (state, collectionKey, query, onDataReceivedCb) => {
      const loadingKey = `${e.LOAD_DELIVERY_ATTRIBUTES}-${collectionKey}`

      state.loading.set(loadingKey, true)

      return {
        graphQuery: {
          query,
          variables: {
            projectId: state.activeProject.id,
          },
          onSuccess: [
            e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
            loadingKey,
            onDataReceivedCb,
          ],
          onError: [e.REQUEST_ERROR, e.LOAD_DELIVERY_ATTRIBUTES],
        },
      }
    },
  )

  eventsStore.on(e.REGISTER_SERVICE_WORKER, () => {
    if (!('serviceWorker' in navigator)) {
      return
    }

    if (window.navigator) {
      navigator.serviceWorker.register('/sw.js')
    }
  })

  eventsStore.on(e.RESET_INPUT_STATUSES, () => {
    // only postcallback
  })

  eventsStore.on(e.GET_CURRENT_USER, state => {
    state.loading.set(e.GET_CURRENT_USER, true)

    return {
      graphQuery: {
        query: GetCurrentUserDocument,
        variables: {},
        onSuccess: [e.GET_CURRENT_USER_SUCCESS],
        onError: [e.GET_CURRENT_USER_ERROR],
      },
    }
  })

  eventsStore.on(
    e.GET_CURRENT_USER_SUCCESS,
    (state, { getCurrentUser }: IQuery) => {
      state.loading.set(e.GET_CURRENT_USER, false)

      const userFromDto = User.fromDto(getCurrentUser)
      if (!userFromDto) {
        console.warn(
          'GET_AUTH_SUCCESS user is null or empty! Will go to GET_AUTH_USER_ERROR',
        )
        // Go back to login screen if user data is missing
        return {
          dispatch: [e.GET_CURRENT_USER_ERROR],
        }
      }

      state.user = userFromDto

      if (!isLoginBundle()) {
        rootStore.chatService.init(getCurrentUser.id)
      }
    },
  )

  eventsStore.on(e.GET_CURRENT_USER_ERROR, () => {
    console.error(
      'Failed to obtain user data, aborting initialization. Try logging in again.',
    )

    return {
      dispatch: [e.LOGOUT],
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_BASEMAPS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_BASEMAPS, true)

    return {
      graphQuery: {
        query: BasemapsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.BASEMAPS_RECIEVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_BASEMAPS],
      },
    }
  })

  eventsStore.on(e.BASEMAPS_RECIEVED, (state, { basemaps }: IQuery) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_BASEMAPS, false)

    rootStore.basemapsStore.receiveList(basemaps?.data || [])

    return {
      graphSubscription: {
        query: ListenBasemapsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.BASEMAP_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REPORT_ERROR, e.BASEMAPS_RECIEVED],
      },
    }
  })

  eventsStore.on(e.BASEMAP_UPDATED, (_, { basemap }: ISubscription) => {
    if (!basemap) {
      return
    }

    rootStore.basemapsStore.receiveOne(basemap.id, basemap.item)
  })

  eventsStore.on(e.LISTEN_TO_SHARED_SITE_PERMITS, state => {
    return {
      graphSubscription: {
        query: ListenToSharedSitePermitsDocument,
        variables: {
          projectToId: state.activeProject.id,
        },
        onData: [e.SHARED_SITE_PERMIT_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SHARED_SITE_PERMITS],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_SHARED_DELIVERIES, state => {
    return {
      graphSubscription: {
        query: ListenToSharedDeliveriesDocument,
        variables: {
          projectToId: state.activeProject.id,
        },
        onData: [e.SHARED_DELIVERIES_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SHARED_DELIVERIES],
      },
    }
  })

  eventsStore.on(e.GET_SHARED_SITE_PERMITS, state => {
    state.loading.set(e.GET_SHARED_SITE_PERMITS, false)

    return {
      graphQuery: {
        query: GetSharedSitePermitsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SHARED_SITE_PERMITS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_SHARED_SITE_PERMITS],
      },
    }
  })

  eventsStore.on(e.GET_SHARED_DELIVERIES, state => {
    state.loading.set(e.GET_SHARED_DELIVERIES, false)

    return {
      graphQuery: {
        query: GetSharedDeliveriesDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SHARED_DELIVERIES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_SHARED_DELIVERIES],
      },
    }
  })

  eventsStore.on(
    e.SHARED_SITE_PERMIT_UPDATED,
    (state, { sharedSitePermit }: ISubscription) => {
      if (!sharedSitePermit) {
        return
      }

      rootStore.sharedSitePermitStore.receiveOne(
        sharedSitePermit.id,
        sharedSitePermit.item,
      )
    },
  )

  eventsStore.on(
    e.SHARED_DELIVERIES_UPDATED,
    (state, { sharedDelivery }: ISubscription) => {
      if (!sharedDelivery) {
        return
      }

      rootStore.sharedDeliveriesStore.receiveOne(
        sharedDelivery.id,
        sharedDelivery.item,
      )
    },
  )

  eventsStore.on(
    e.SHARED_SITE_PERMITS_RECEIVED,
    (state, { getSharedSitePermitsByProjectToId }: IQuery) => {
      state.loading.set(e.GET_SHARED_SITE_PERMITS, false)

      rootStore.sharedSitePermitStore.receiveList(
        getSharedSitePermitsByProjectToId || [],
      )
    },
  )

  eventsStore.on(
    e.SHARED_DELIVERIES_RECEIVED,
    (state, { getSharedDeliveriesByProjectToId }: IQuery) => {
      state.loading.set(e.GET_SHARED_SITE_PERMITS, false)

      rootStore.sharedDeliveriesStore.receiveList(
        getSharedDeliveriesByProjectToId || [],
      )
    },
  )

  eventsStore.on(e.GLOBE_VIEWS_RECEIVED, (state, { globeViews }: IQuery) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_GLOBE_VIEWS, false)

    rootStore.globeViewsStore.receiveList(globeViews?.data || [])

    return {
      graphSubscription: {
        query: ListenGlobeViewsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.GLOBE_VIEW_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REPORT_ERROR, e.GLOBE_VIEWS_RECEIVED],
      },
    }
  })

  eventsStore.on(
    e.GLOBE_VIEW_UPDATED,
    (state, { globeView }: ISubscription) => {
      state.loading.set(e.SAVE_GLOBE_VIEW, false)
      if (!globeView) {
        return
      }

      rootStore.globeViewsStore.receiveOne(globeView.id, globeView.item)
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_TILESETS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_TILESETS, true)

    return {
      graphQuery: {
        query: GetMapboxTilesetsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.TILESETS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_TILESETS],
      },
    }
  })

  eventsStore.on(
    e.TILESETS_RECEIVED,
    (state, { getMapboxTilesets }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_TILESETS, false)

      rootStore.tilesetsStore.receiveList(getMapboxTilesets || [])

      return {
        graphSubscription: {
          query: ListenTilesetsByProjectIdDocument,
          variables: {
            projectId: state.activeProject.id,
          },
          onData: [e.TILESET_UPDATED],
          onSuccess: [e.NO_EFFECT],
          onError: [e.REPORT_ERROR, e.TILESETS_RECEIVED],
        },
      }
    },
  )

  eventsStore.on(e.TILESET_UPDATED, (_, { tileset }: ISubscription) => {
    if (!tileset) {
      return
    }

    rootStore.tilesetsStore.receiveOne(tileset.id, tileset.item)
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_REMOTE_TILESETS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_REMOTE_TILESETS, true)
    return {
      graphQuery: {
        query: GetRemoteTilesetsDocument,
        variables: { projectId: state.activeProject.id },
        onSuccess: [e.REMOTE_TILESETS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_REMOTE_TILESETS],
      },
    }
  })

  eventsStore.on(e.REMOTE_TILESETS_RECEIVED, (state, { remoteTilesets }) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_REMOTE_TILESETS, false)

    rootStore.remoteTilesetsStore.setList(remoteTilesets?.data || [])

    return {
      graphSubscription: {
        query: ListenRemoteTilesetsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.REMOTE_TILESETS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REPORT_ERROR, e.REMOTE_TILESETS_RECEIVED],
      },
    }
  })

  eventsStore.on(
    e.REMOTE_TILESETS_UPDATED,
    (_, { remoteTileset }: ISubscription) => {
      if (!remoteTileset) {
        return
      }
      rootStore.remoteTilesetsStore.receiveOne(
        remoteTileset.id,
        remoteTileset.item,
      )
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SITEMAPS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SITEMAPS, true)

    return {
      graphQuery: {
        query: SitemapsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SITEMAPS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_SITEMAPS],
      },
    }
  })

  eventsStore.on(e.SITEMAPS_RECEIVED, (state, { sitemaps }: IQuery) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SITEMAPS, false)

    rootStore.sitemapsStore.receiveList(sitemaps?.data || [])

    return {
      graphSubscription: {
        query: ListenSitemapsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.SITEMAP_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REPORT_ERROR, e.SITEMAPS_RECEIVED],
      },
    }
  })

  eventsStore.on(e.SITEMAP_UPDATED, (_, { sitemap }: ISubscription) => {
    if (!sitemap) {
      return
    }

    rootStore.sitemapsStore.receiveOne(sitemap.id, sitemap.item)
  })

  eventsStore.on(e.LISTEN_TO_SITEMAP_ITEMS_DATA, state => {
    return {
      graphSubscription: {
        query: ListenSitemapItemDataByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.SITEMAP_ITEM_DATA_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SITEMAP_ITEMS_DATA],
      },
    }
  })

  eventsStore.on(
    e.SITEMAP_ITEM_DATA_UPDATED,
    (state, { sitemapSpecificItemData }: ISubscription) => {
      if (!sitemapSpecificItemData) {
        return
      }

      rootStore.sitemapsStore.receiveOneItem(
        sitemapSpecificItemData.id,
        sitemapSpecificItemData.item,
      )
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_GLOBE_VIEWS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_GLOBE_VIEWS, true)

    return {
      graphQuery: {
        query: GlobeViewsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.GLOBE_VIEWS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_GLOBE_VIEWS],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_GLOBE_VIEW_ITEMS_DATA, state => {
    return {
      graphSubscription: {
        query: ListenGlobeViewItemsDataByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.GLOBE_VIEW_ITEM_DATA_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_GLOBE_VIEW_ITEMS_DATA],
      },
    }
  })

  eventsStore.on(
    e.GLOBE_VIEW_ITEM_DATA_UPDATED,
    (state, { globeViewSpecificItemData }: ISubscription) => {
      if (!globeViewSpecificItemData) {
        return
      }

      rootStore.globeViewsStore.receiveOneItem(
        globeViewSpecificItemData.id,
        globeViewSpecificItemData.item,
      )
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SITEMAP_ITEMS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SITEMAP_ITEMS, true)

    return {
      graphQuery: {
        query: SitemapItemsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SITEMAP_ITEMS_RECIEVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_SITEMAP_ITEMS],
      },
    }
  })

  eventsStore.on(
    e.SITEMAP_ITEMS_RECIEVED,
    (state, { sitemapItems }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_SITEMAP_ITEMS, false)

      rootStore.sitemapItemsStore.receiveList(sitemapItems?.data || [])

      return {
        graphSubscription: {
          query: ListenSitemapItemsByProjectIdDocument,
          variables: {
            projectId: state.activeProject.id,
          },
          onData: [e.SITEMAP_ITEM_UPDATED],
          onSuccess: [e.NO_EFFECT],
          onError: [e.REPORT_ERROR, e.SITEMAP_ITEMS_RECIEVED],
        },
      }
    },
  )

  eventsStore.on(
    e.SITEMAP_ITEM_UPDATED,
    (_, { sitemapItem }: ISubscription) => {
      if (!sitemapItem) {
        return
      }

      rootStore.sitemapItemsStore.receiveOne(sitemapItem.id, sitemapItem.item)
    },
  )

  eventsStore.on(e.LOAD_MARKUPS, (state, sitePermitIds?: string[]) => {
    state.loading.set(e.LOAD_MARKUPS, true)

    return {
      graphQuery: {
        query: MarkupsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
          sitePermitIds: sitePermitIds ?? null,
        },
        // Thread the requested workflows so the handler marks them loaded (incl. empties).
        onSuccess: [e.MARKUPS_RECIEVED, sitePermitIds ?? null],
        onError: [e.REQUEST_ERROR], // no retry: a bare reload would fetch the whole project
      },
    }
  })

  eventsStore.on(
    e.MARKUPS_RECIEVED,
    (state, sitePermitIds: string[] | null, { markups }: IQuery) => {
      state.loading.set(e.LOAD_MARKUPS, false)

      rootStore.markupsStore.mergeList(markups?.data || [], sitePermitIds ?? [])
    },
  )

  // Project switch: drop the old project's markups; views re-load lazily.
  eventsStore.on(e.CLEAR_MARKUPS, () => {
    rootStore.markupsStore.clearList()
  })

  // Project-scoped subscription; MARKUP_UPDATED relevance-gates each event.
  eventsStore.on(e.LISTEN_TO_MARKUPS, state => ({
    graphSubscription: {
      query: ListenMarkupsByProjectIdDocument,
      variables: {
        projectId: state.activeProject.id,
      },
      onData: [e.MARKUP_UPDATED],
      onSuccess: [e.NO_EFFECT],
      onError: [e.REPORT_ERROR, e.LISTEN_TO_MARKUPS],
    },
  }))

  eventsStore.on(e.MARKUP_UPDATED, (_, { markup }: ISubscription) => {
    if (!markup) return

    const ms = rootStore.markupsStore
    // Soft-delete arrives as an isDeleted=true update, never a real delete — treat as removal.
    if (!markup.item || markup.item.isDeleted) {
      ms.removeLocal([markup.id])
      return
    }
    // Relevance gate: only apply for a workflow in the current view.
    if (ms.isSitePermitLoaded(markup.item.sitePermitId)) {
      ms.receiveOne(markup.id, markup.item)
    }
  })

  eventsStore.on(e.INVALIDATE_LOCATION_LOAD, () => {
    locationLoadGeneration += 1
    workflowRestrictionGeneration += 1
  })

  eventsStore.on(e.LOAD_LOCATIONS_AND_LISTEN_CHANGES, state => {
    state.loading.set(e.LOAD_LOCATIONS_AND_LISTEN_CHANGES, true)
    const requestedProjectId = state.activeProject.id
    const generation = ++locationLoadGeneration
    return {
      graphQuery: {
        query: LocationsByProjectIdDocument,
        variables: {
          projectId: requestedProjectId,
        },
        onSuccess: [e.LOAD_LOCATIONS_SUCCESS, requestedProjectId, generation],
        onError: [e.LOAD_LOCATIONS_FAILURE, requestedProjectId, generation],
      },
    }
  })

  eventsStore.on(e.LOAD_RESOURCE_SCHEMAS, state => {
    const projectId = state.activeProject.id
    return {
      dispatchN: [
        [e.LISTEN_TO_RESOURCE_PROPERTIES],
        [e.LISTEN_TO_RESOURCE_TEMPLATES],
      ],
      cmd: {
        cmd: {
          execute: () =>
            Promise.all([
              rootStore.equipmentResourcesStore.ensureSchema(projectId),
              rootStore.materialResourcesStore.ensureSchema(projectId),
              rootStore.sensorResourcesStore.ensureSchema(projectId),
              rootStore.locationResourcesStore.ensureSchema(projectId),
            ]),
        },
        onSuccess: [e.NO_EFFECT],
        onError: [e.NO_EFFECT],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_RESOURCE_PROPERTIES, (state: InitialState) => ({
    graphSubscription: {
      query: ListenToResourcePropertiesDocument,
      variables: { projectId: state.activeProject.id },
      listenMany: true,
      onData: [e.REFRESH_RESOURCE_SCHEMAS],
      onSuccess: [e.NO_EFFECT],
      onError: [e.REQUEST_ERROR, e.LISTEN_TO_RESOURCE_PROPERTIES],
    },
  }))

  eventsStore.on(e.LISTEN_TO_RESOURCE_TEMPLATES, (state: InitialState) => ({
    graphSubscription: {
      query: ListenToResourceTemplatesDocument,
      variables: { projectId: state.activeProject.id },
      listenMany: true,
      onData: [e.REFRESH_RESOURCE_SCHEMAS],
      onSuccess: [e.NO_EFFECT],
      onError: [e.REQUEST_ERROR, e.LISTEN_TO_RESOURCE_TEMPLATES],
    },
  }))

  eventsStore.on(
    e.REFRESH_RESOURCE_SCHEMAS,
    (state: InitialState, changes: ISubscription[]) => {
      const projectId = state.activeProject.id
      const resourceTypeIds = new Set(
        changes.map(
          change =>
            change.resourceProperty?.item?.resourceTypeId ??
            change.resourceTemplate?.item?.resourceTypeId,
        ),
      )
      const refreshAll = resourceTypeIds.has(undefined)
      const stores = [
        [
          ResourceTypeId.OFFLOADING_EQUIPMENT,
          rootStore.equipmentResourcesStore,
        ],
        [ResourceTypeId.MATERIALS, rootStore.materialResourcesStore],
        [ResourceTypeId.SENSORS, rootStore.sensorResourcesStore],
        [ResourceTypeId.LOCATIONS, rootStore.locationResourcesStore],
      ] as const
      return {
        cmd: {
          cmd: {
            execute: () =>
              Promise.all(
                stores
                  .filter(
                    ([typeId]) => refreshAll || resourceTypeIds.has(typeId),
                  )
                  .map(([, store]) => store.refreshSchema(projectId)),
              ),
          },
          onSuccess: [e.NO_EFFECT],
          onError: [e.NO_EFFECT],
        },
      }
    },
  )

  eventsStore.on(
    e.LOAD_LOCATIONS_FAILURE,
    (state, requestedProjectId: string, generation: number, cause: unknown) => {
      if (
        state.activeProject.id !== requestedProjectId ||
        generation !== locationLoadGeneration
      ) {
        return
      }
      state.loading.set(e.LOAD_LOCATIONS_AND_LISTEN_CHANGES, false)
      rootStore.equipmentResourcesStore.receiveFailure(
        requestedProjectId,
        cause,
      )
      rootStore.materialResourcesStore.receiveFailure(requestedProjectId, cause)
      rootStore.sensorResourcesStore.receiveFailure(requestedProjectId, cause)
      rootStore.locationResourcesStore.receiveFailure(requestedProjectId, cause)
      return {
        dispatch: [e.REQUEST_ERROR, e.LOAD_LOCATIONS_AND_LISTEN_CHANGES, cause],
      }
    },
  )

  eventsStore.on(
    e.LOAD_LOCATIONS_SUCCESS,
    (
      state,
      requestedProjectId: string,
      generation: number,
      { locations }: IQuery,
    ) => {
      if (
        state.activeProject.id !== requestedProjectId ||
        generation !== locationLoadGeneration
      ) {
        return
      }
      state.loading.set(e.LOAD_LOCATIONS_AND_LISTEN_CHANGES, false)

      const items = locations.data || []
      const itemsByType = {}
      items.forEach(item => {
        const key = locationRoutingKey(item)
        if (!itemsByType[key]) {
          itemsByType[key] = []
        }
        itemsByType[key].push(item)
      })

      // Alphabetically sorting by name
      Object.keys(itemsByType).forEach(key => {
        itemsByType[key].sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name),
        )
      })

      rootStore.buildingsStore.receiveList(
        itemsByType[LocationKind.Building] || [],
      )
      rootStore.gatesStore.receiveList(itemsByType[LocationKind.Gate] || [])
      rootStore.zonesStore.receiveList(itemsByType[LocationKind.Zone] || [])
      rootStore.routesStore.receiveList(itemsByType[LocationKind.Route] || [])
      rootStore.offloadingEquipmentsStore.receiveList(
        itemsByType[LocationType.OffloadingEquipment] || [],
      )
      rootStore.equipmentResourcesStore.receiveList(
        requestedProjectId,
        itemsByType[LocationType.OffloadingEquipment] || [],
      )
      rootStore.materialResourcesStore.receiveList(
        requestedProjectId,
        itemsByType[LocationType.Material] || [],
      )
      rootStore.sensorResourcesStore.receiveList(
        requestedProjectId,
        itemsByType[LocationType.Sensor] || [],
      )
      // Not a single bucket: a location resource is any row with a kind, which spans the
      // twelve kind buckets today and the collapsed LocationType.Location once the
      // migration runs. decodeLocationResources owns that test, so it is handed the whole
      // page rather than a list of enum members this call site would have to keep in sync.
      rootStore.locationResourcesStore.receiveList(requestedProjectId, items)
      rootStore.levelsStore.receiveList(itemsByType[LocationKind.Level] || [])
      rootStore.areasStore.receiveList(itemsByType[LocationKind.Area] || [])
      rootStore.logisticsObjectsStore.receiveList(
        itemsByType[LocationKind.InformationPoints] || [],
      )
      rootStore.verticalObjectsStore.receiveList(
        itemsByType[LocationKind.VerticalObject] || [],
      )
      rootStore.locationIntegrationsStore.receiveList(
        itemsByType[LocationType.Integration] || [],
      )
      rootStore.stagingsStore.receiveList(
        itemsByType[LocationKind.Staging] || [],
      )
      rootStore.interiorDoorsStore.receiveList(
        itemsByType[LocationKind.InteriorDoor] || [],
      )
      rootStore.interiorPathsStore.receiveList(
        itemsByType[LocationKind.InteriorPath] || [],
      )
      rootStore.projectLocationsStore.receiveList(
        itemsByType[LocationKind.ProjectLocation] || [],
      )
      rootStore.otherLocationsStore.receiveList(
        itemsByType[OTHER_LOCATIONS_ROUTING_KEY] || [],
      )

      return {
        graphSubscription: {
          query: ListenLocationsByProjectIdDocument,
          variables: {
            projectId: requestedProjectId,
          },
          onData: [e.LOCATION_RECEIVED, requestedProjectId, generation],
          onSuccess: [e.NO_EFFECT],
          onError: [e.REPORT_ERROR, e.LOAD_LOCATIONS_SUCCESS],
        },
      }
    },
  )

  eventsStore.on(
    e.LOCATION_RECEIVED,
    (
      state,
      subscriptionProjectId: string,
      generation: number,
      { location }: ISubscription,
    ) => {
      if (
        state.activeProject.id !== subscriptionProjectId ||
        generation !== locationLoadGeneration
      ) {
        return
      }
      const id = location.id ?? location.item?.id
      if (id === null || id === undefined) return

      const projectId = location.item?.projectId ?? subscriptionProjectId
      if (!location.item) {
        rootStore.equipmentResourcesStore.receiveOne(
          String(projectId),
          String(id),
          null,
        )
        rootStore.materialResourcesStore.receiveOne(
          String(projectId),
          String(id),
          null,
        )
        rootStore.sensorResourcesStore.receiveOne(
          String(projectId),
          String(id),
          null,
        )
        rootStore.locationResourcesStore.receiveOne(
          String(projectId),
          String(id),
          null,
        )
        return
      }

      const item = location.item as ILocationDto

      // One call for all twelve kinds rather than an arm in each case below:
      // decodeLocationResources admits a row by whether it has a kind at all, so a delta
      // for any kind - and for the collapsed LocationType.Location once the migration
      // runs - routes correctly without this switch having to know which is which.
      rootStore.locationResourcesStore.receiveOne(
        String(projectId),
        String(id),
        location.item,
      )

      // The kind is editable: reclassifying a zone as a building routes the delta to
      // buildingsStore while the row stays in zonesStore, and the surfaces that concat those
      // stores then show the location twice. Only the legacy per-kind stores need this - the
      // resource stores are keyed by id alone, and the enum-routed resource types cannot
      // change bucket, since no save path can write LocationType.
      const dropStaleKindEntries = staleKindEntriesCleanup(
        rootStore,
        String(id),
      )

      // Switched on the routing key, not the raw type: the twelve kind arms are keyed by
      // kind slug so a migrated row - which reports LocationType.Location - still reaches
      // its store, while the four resource-type arms fall through to their enum value, and
      // a place with no known root reaches the catch-all arm.
      switch (locationRoutingKey(location.item)) {
        case LocationKind.Building: {
          rootStore.buildingsStore.receiveOne(item)
          break
        }
        case LocationKind.Zone: {
          rootStore.zonesStore.receiveOne(item)
          break
        }
        case LocationKind.Gate: {
          rootStore.gatesStore.receiveOne(item)
          break
        }
        case LocationKind.Route: {
          rootStore.routesStore.receiveOne(item)
          break
        }
        case LocationType.OffloadingEquipment: {
          rootStore.offloadingEquipmentsStore.receiveOne(item)
          rootStore.equipmentResourcesStore.receiveOne(
            String(projectId),
            String(id),
            location.item,
          )
          break
        }
        case LocationType.Material: {
          rootStore.materialResourcesStore.receiveOne(
            String(projectId),
            String(id),
            location.item,
          )
          break
        }
        case LocationType.Sensor: {
          rootStore.sensorResourcesStore.receiveOne(
            String(projectId),
            String(id),
            location.item,
          )
          break
        }
        case LocationKind.Level: {
          rootStore.levelsStore.receiveOne(item)
          break
        }
        case LocationKind.Area: {
          rootStore.areasStore.receiveOne(item)
          break
        }
        case LocationKind.InformationPoints: {
          rootStore.logisticsObjectsStore.receiveOne(item)
          break
        }
        case LocationKind.VerticalObject: {
          rootStore.verticalObjectsStore.receiveOne(item)
          break
        }
        case LocationType.Integration: {
          rootStore.locationIntegrationsStore.receiveOne(item)
          break
        }
        case LocationKind.Staging: {
          rootStore.stagingsStore.receiveOne(item)
          break
        }
        case LocationKind.InteriorDoor: {
          rootStore.interiorDoorsStore.receiveOne(item)
          break
        }
        case LocationKind.InteriorPath: {
          rootStore.interiorPathsStore.receiveOne(item)
          break
        }
        case LocationKind.ProjectLocation: {
          rootStore.projectLocationsStore.receiveOne(item)
          break
        }
        case OTHER_LOCATIONS_ROUTING_KEY: {
          rootStore.otherLocationsStore.receiveOne(item)
          break
        }
        default: {
          // Reached only by a row that is neither a place nor a resource type this build
          // knows - a place with no known kind lands in the catch-all arm above. It used to
          // fall off the end of the switch and be dropped in total silence; say so instead,
          // because the symptom otherwise is a location that simply never appears.
          // eslint-disable-next-line no-console
          console.warn(
            `Location ${String(id)} has no routable kind (type=${String(
              location.item.type,
            )}); it will not appear in kind-keyed views.`,
          )
        }
      }

      dropStaleKindEntries()
    },
  )

  eventsStore.on(e.LISTEN_AND_LOAD_DELIVERIES, state => {
    state.loading.set(e.LISTEN_AND_LOAD_DELIVERIES, true)

    return {
      graphSubscription: {
        query: ListenDeliveriesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.DELIVERY_RECEIVED],
        onSuccess: [
          e.COMPLETE_REQUEST_AND_DISPATCH,
          e.LISTEN_AND_LOAD_DELIVERIES,
          e.LOAD_DELIVERIES,
        ],
        onError: [e.REQUEST_ERROR, e.LISTEN_AND_LOAD_DELIVERIES],
      },
    }
  })

  eventsStore.on(
    e.LOAD_DELIVERIES_SUCCESS,
    (state, { deliveries: { data: deliveries } }: IQuery) => {
      state.loading.set(e.LOAD_DELIVERIES, false)
      rootStore.deliveriesStore.receiveList(deliveries)

      setLatestDeliveryByProjectAndUser(rootStore)

      return {
        dispatchN: [[e.TRIGGER_CALCULATE_DELIVERY_TREE]],
      }
    },
  )
  eventsStore.on(
    e.DELIVERY_RECEIVED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ delivery: { item: dto } }) => {
        rootStore.deliveriesStore.updateOne(dto.id, dto)

        updateDisplayedDeliveryIfNeed(rootStore, dto.id)
      })

      setLatestDeliveryByProjectAndUser(rootStore)

      return {
        dispatchN: [[e.TRIGGER_CALCULATE_DELIVERY_TREE]],
      }
    },
  )

  eventsStore.on(e.LOAD_CLOSURES_AND_LISTEN_CHANGES, state => {
    return {
      graphQuery: {
        query: GetClosuresListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.LOAD_CLOSURES_SUCCESS],
        onError: [e.REQUEST_ERROR, e.LOAD_CLOSURES_AND_LISTEN_CHANGES],
      },
    }
  })

  eventsStore.on(e.LOAD_CLOSURES_SUCCESS, (_, { closures }: IQuery) => {
    rootStore.closuresStore.receiveList(closures.data)
    return {
      dispatch: [e.LISTEN_TO_CLOSURES],
    }
  })

  eventsStore.on(e.LISTEN_TO_CLOSURES, state => {
    return {
      graphSubscription: {
        query: ListenToClosuresDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.CLOSURE_RECEIVED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_CLOSURES],
      },
    }
  })

  eventsStore.on(
    e.CLOSURE_RECEIVED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ closure }) => {
        if (closure) {
          rootStore.closuresStore.receiveOne(closure.id, closure.item)
        }
      })
    },
  )

  eventsStore.on(
    e.LOAD_AND_LISTEN_TO_SITE_PERMIT_STATUS_CHANGES,
    (state: InitialState, formId: string, callbackFn: () => void) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_STATUS_CHANGES, true)

      return {
        graphQuery: {
          query: GetSitePermitStatusChangeListDocument,
          variables: {
            projectId: state.activeProject.id,
            permitId: formId,
          },
          onSuccess: [
            e.SITE_PERMIT_STATUS_CHANGES_RECEIVED,
            formId,
            callbackFn,
          ],
          onError: [
            e.REQUEST_ERROR,
            e.LOAD_AND_LISTEN_TO_SITE_PERMIT_STATUS_CHANGES,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.SITE_PERMIT_STATUS_CHANGES_RECEIVED,
    (
      state: InitialState,
      formId: string,
      callbackFn: () => void,
      { sitePermitStatusChanges }: IQuery,
    ) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_STATUS_CHANGES, false)
      if (!rootStore.permitStatusChangesStore.isServing(formId)) return

      rootStore.permitStatusChangesStore.receiveList(
        sitePermitStatusChanges.data,
      )

      return {
        dispatch: [e.LISTEN_TO_SITE_PERMIT_STATUS_CHANGES, formId, callbackFn],
      }
    },
  )

  eventsStore.on(
    e.LISTEN_TO_SITE_PERMIT_STATUS_CHANGES,
    (state: InitialState, formId: string, callbackFn: () => void) => {
      return {
        graphSubscription: {
          query: ListenToSitePermitStatusChangeDocument,
          variables: {
            projectId: state.activeProject.id,
            permitId: formId,
          },
          listenMany: true,
          onData: [e.SITE_PERMIT_STATUS_CHANGE_UPDATED],
          onSuccess: [e.RUN_CALLBACK, callbackFn],
          onError: [e.REQUEST_ERROR, e.LISTEN_TO_SITE_PERMIT_STATUS_CHANGES],
        },
      }
    },
  )

  eventsStore.on(
    e.SITE_PERMIT_STATUS_CHANGE_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ sitePermitStatusChange }) => {
        if (sitePermitStatusChange) {
          rootStore.permitStatusChangesStore.receiveOne(
            sitePermitStatusChange.id,
            sitePermitStatusChange.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTIONS, state => {
    return {
      graphQuery: {
        query: GetSitePermitInspectionListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SITE_PERMIT_INSPECTIONS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTIONS,
        ],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_INSPECTIONS_RECEIVED,
    (_, { sitePermitInspections }: IQuery) => {
      rootStore.permitInspectionsStore.receiveList(sitePermitInspections.data)

      return {
        dispatch: [e.LISTEN_TO_SITE_PERMIT_INSPECTIONS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_SITE_PERMIT_INSPECTIONS, state => {
    return {
      graphSubscription: {
        query: ListenToSitePermitInspectionDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.SITE_PERMIT_INSPECTION_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SITE_PERMIT_INSPECTIONS],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_INSPECTION_UPDATED,
    (_: InitialState, { sitePermitInspection }: ISubscription) => {
      if (!sitePermitInspection) {
        return
      }

      rootStore.permitInspectionsStore.receiveOne(
        sitePermitInspection.id,
        sitePermitInspection.item,
      )
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES, state => {
    return {
      graphQuery: {
        query: GetSitePermitInspectionChangeListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SITE_PERMIT_INSPECTION_CHANGES_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES,
        ],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_INSPECTION_CHANGES_RECEIVED,
    (_, { sitePermitInspectionChanges }: IQuery) => {
      rootStore.permitInspectionChangesStore.receiveList(
        sitePermitInspectionChanges.data,
      )

      return {
        dispatch: [e.LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES, state => {
    return {
      graphSubscription: {
        query: ListenToSitePermitInspectionChangeDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.SITE_PERMIT_INSPECTION_CHANGE_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_INSPECTION_CHANGE_UPDATED,
    (_: InitialState, { sitePermitInspectionChange }: ISubscription) => {
      if (!sitePermitInspectionChange) {
        return
      }

      rootStore.permitInspectionChangesStore.receiveOne(
        sitePermitInspectionChange.id,
        sitePermitInspectionChange.item,
      )
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SITE_PERMITS_FOLLOWINGS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SITE_PERMITS_FOLLOWINGS, true)

    return {
      graphQuery: {
        query: GetSitePermitFollowingListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SITE_PERMIT_FOLLOWINGS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_SITE_PERMITS_FOLLOWINGS,
        ],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_FOLLOWINGS_RECEIVED,
    (state, { sitePermitFollowings }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_SITE_PERMITS_FOLLOWINGS, false)

      rootStore.workflowFollowingsStore.receiveList(
        (sitePermitFollowings?.data as IAssociationDto[]) || [],
      )

      return {
        dispatch: [e.LISTEN_TO_SITE_PERMIT_FOLLOWING],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_SITE_PERMIT_FOLLOWING, state => {
    return {
      graphSubscription: {
        query: ListenToSitePermitFollowingDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.SITE_PERMIT_FOLLOWING_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SITE_PERMIT_FOLLOWING],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_FOLLOWING_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ sitePermitFollowing }) => {
        if (sitePermitFollowing) {
          rootStore.workflowFollowingsStore.receiveOne(
            sitePermitFollowing.id,
            sitePermitFollowing.item as IAssociationDto,
          )
        }
      })
    },
  )

  eventsStore.on(e.GET_MATURIX_CONFIGURATION, state => {
    state.loading.set(e.GET_MATURIX_CONFIGURATION, true)

    return {
      graphQuery: {
        query: ConfigurationByNameDocument,
        variables: {
          name: `project.${state.activeProject.id}.integrations.maturix`,
        },
        onSuccess: [e.GET_MATURIX_CONFIGURATION_SUCCESS],
        onError: [e.REQUEST_ERROR, e.GET_MATURIX_CONFIGURATION],
      },
    }
  })

  eventsStore.on(
    e.GET_MATURIX_CONFIGURATION_SUCCESS,
    (state, { configuration }: IQuery) => {
      state.loading.set(e.GET_MATURIX_CONFIGURATION, false)
      if (configuration?.value) {
        rootStore.maturixProjectsStore.receiveConfig(configuration?.value)
      } else {
        rootStore.maturixProjectsStore.clearConfig()
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_MATERIALS, (state: InitialState) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIALS, true)

    const { id, materialsUploadId } = state.activeProject

    return {
      graphQuery: {
        query: GetMaterialListDocument,
        variables: { projectId: id, materialsUploadId },
        onSuccess: [e.MATERIALS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_MATERIALS],
      },
    }
  })

  eventsStore.on(
    e.MATERIALS_RECEIVED,
    (state: InitialState, { projectMaterials }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIALS, false)

      rootStore.materialsStore.receiveList(projectMaterials?.data || [])

      return {
        dispatch: [e.LISTEN_TO_MATERIAL],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_MATERIAL, (state: InitialState) => {
    const { id, materialsUploadId } = state.activeProject
    return {
      graphSubscription: {
        query: ListenToMaterialDocument,
        variables: { projectId: id, materialsUploadId },
        listenMany: true,
        onData: [e.MATERIAL_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_MATERIAL],
      },
    }
  })

  eventsStore.on(
    e.MATERIAL_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ projectMaterial }) => {
        if (projectMaterial) {
          rootStore.materialsStore.receiveOne(
            projectMaterial.id,
            projectMaterial.item,
          )
        }
      })
    },
  )

  eventsStore.on(
    e.LOAD_AND_LISTEN_TO_MATERIAL_CONFIGURATION,
    (state: InitialState) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIAL_CONFIGURATION, true)

      const { id } = state.activeProject

      return {
        graphQuery: {
          query: GetMaterialConfigurationDocument,
          variables: { projectId: id },
          onSuccess: [e.MATERIAL_CONFIGURATION_RECEIVED],
          onError: [
            e.REQUEST_ERROR,
            e.LOAD_AND_LISTEN_TO_MATERIAL_CONFIGURATION,
          ],
        },
      }
    },
  )

  eventsStore.on(
    e.MATERIAL_CONFIGURATION_RECEIVED,
    (state: InitialState, { materialConfiguration }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIAL_CONFIGURATION, false)

      rootStore.materialConfigurationStore.updateMaterialConfiguration(
        materialConfiguration,
      )

      return {
        dispatch: [e.LISTEN_TO_MATERIAL_CONFIGURATION],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_MATERIAL_CONFIGURATION, (state: InitialState) => {
    const { id } = state.activeProject
    return {
      graphSubscription: {
        query: ListenToMaterialConfigurationDocument,
        variables: { projectId: id },
        listenMany: false,
        onData: [e.MATERIAL_CONFIGURATION_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_MATERIAL_CONFIGURATION],
      },
    }
  })

  eventsStore.on(
    e.MATERIAL_CONFIGURATION_UPDATED,
    (_: InitialState, { materialConfiguration }: ISubscription) => {
      rootStore.materialConfigurationStore.updateMaterialConfiguration(
        materialConfiguration?.item,
      )
    },
  )

  eventsStore.on(
    e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES,
    (state: InitialState) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES, true)

      const { id, materialsUploadId } = state.activeProject

      return {
        graphQuery: {
          query: GetMaterialCategoryListDocument,
          variables: { projectId: id, materialsUploadId },
          onSuccess: [e.MATERIAL_CATEGORIES_RECEIVED],
          onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES],
        },
      }
    },
  )

  eventsStore.on(
    e.MATERIAL_CATEGORIES_RECEIVED,
    (state: InitialState, { projectMaterialCategories }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES, false)

      rootStore.materialCategoryStore.receiveList(
        projectMaterialCategories?.data || [],
      )

      return {
        dispatch: [e.LISTEN_TO_MATERIAL_CATEGORY],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_MATERIAL_CATEGORY, (state: InitialState) => {
    const { id, materialsUploadId } = state.activeProject

    return {
      graphSubscription: {
        query: ListenToMaterialCategoryDocument,
        variables: { projectId: id, materialsUploadId },
        listenMany: true,
        onData: [e.MATERIAL_CATEGORY_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_MATERIAL_CATEGORY],
      },
    }
  })

  eventsStore.on(
    e.MATERIAL_CATEGORY_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ projectMaterialCategory }) => {
        if (projectMaterialCategory) {
          rootStore.materialCategoryStore.receiveOne(
            projectMaterialCategory.id,
            projectMaterialCategory.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.RUN_CALLBACK, (_, callback, data) => {
    if (callback) {
      callback(data)
    }
  })

  eventsStore.on(e.COLLECT_TELEMETRY, (_, telemetry: ITelemetryPayload) => {
    const { event, timing } = telemetry

    if (event) {
      ReactGA.event({
        category: event.eventCategory,
        action: event.eventAction,
        label: window.location.pathname.split('/')[2],
        value: event.eventValue,
      })
    }
    if (timing) {
      ReactGA.event({
        category: timing.timingCategory,
        action: timing.timingVar,
        label: window.location.pathname.split('/')[2],
        value: timing.timingValue,
      })
    }
  })

  eventsStore.on(e.TRIGGER_CALCULATE_DELIVERY_TREE, () => {
    // service event
    // aim: dispatch CALCULATE_DELIVERY_TREE if needs and required data are received
    // see DeliveryListComponent.store.ts (onTreeRequest)
  })

  eventsStore.on(e.GET_DELIVERY_CUSTOM_FILTERS, state => {
    return {
      graphQuery: {
        query: GetDeliveryCustomFiltersByProjectDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.GET_DELIVERY_CUSTOM_FILTERS_SUCCESS],
        onError: [e.REQUEST_ERROR, e.GET_DELIVERY_CUSTOM_FILTERS],
      },
    }
  })

  eventsStore.on(
    e.GET_DELIVERY_CUSTOM_FILTERS_SUCCESS,
    (_, { deliveryListCustomFilters: { data: filters } }: IQuery) => {
      rootStore.customDeliveryListFiltersStore.receiveList(filters)
    },
  )

  eventsStore.on(e.GET_WORKFLOW_CUSTOM_FILTERS, state => {
    state.loading.set(e.GET_WORKFLOW_CUSTOM_FILTERS, true)

    return {
      graphQuery: {
        query: GetWorkflowCustomFiltersByProjectDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.GET_WORKFLOW_CUSTOM_FILTERS_SUCCESS],
        onError: [e.REQUEST_ERROR, e.GET_WORKFLOW_CUSTOM_FILTERS],
      },
    }
  })

  eventsStore.on(
    e.GET_WORKFLOW_CUSTOM_FILTERS_SUCCESS,
    (state, { workflowsCustomFilters: { data: filters } }: IQuery) => {
      state.loading.set(e.GET_WORKFLOW_CUSTOM_FILTERS, false)

      rootStore.customWorkflowsListFiltersStore.receiveList(filters)
    },
  )

  eventsStore.on(e.GET_UPLOAD_LOG_CUSTOM_FILTERS, state => {
    state.loading.set(e.GET_UPLOAD_LOG_CUSTOM_FILTERS, true)

    return {
      graphQuery: {
        query: GetUploadLogCustomFiltersByProjectDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.GET_UPLOAD_LOG_CUSTOM_FILTERS_SUCCESS],
        onError: [e.REQUEST_ERROR, e.GET_UPLOAD_LOG_CUSTOM_FILTERS],
      },
    }
  })

  eventsStore.on(
    e.GET_UPLOAD_LOG_CUSTOM_FILTERS_SUCCESS,
    (state, { uploadLogCustomFilters: { data: filters } }: IQuery) => {
      state.loading.set(e.GET_UPLOAD_LOG_CUSTOM_FILTERS, false)

      rootStore.customUploadLogFiltersStore.receiveList(filters)
    },
  )

  eventsStore.on(e.GET_UPLOAD_LOG_CONFIGURATION, state => {
    state.loading.set(e.GET_UPLOAD_LOG_CONFIGURATION, true)

    return {
      graphQuery: {
        query: GetUploadLogConfigurationDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.UPLOAD_LOG_CONFIGURATION_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_UPLOAD_LOG_CONFIGURATION],
      },
    }
  })

  eventsStore.on(
    e.UPLOAD_LOG_CONFIGURATION_RECEIVED,
    (state, { uploadLogConfiguration }: IQuery) => {
      state.loading.set(e.GET_UPLOAD_LOG_CONFIGURATION, false)

      if (uploadLogConfiguration) {
        rootStore.uploadLogConfigurationStore.receiveUploadLogConfiguration(
          uploadLogConfiguration,
        )
      }

      return {
        dispatch: [e.LISTEN_TO_UPLOAD_LOG_CONFIGURATION],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_UPLOAD_LOG_CONFIGURATION, state => {
    return {
      graphSubscription: {
        query: ListenToUploadLogConfigurationDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.UPLOAD_LOG_CONFIGURATION_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_UPLOAD_LOG_CONFIGURATION],
      },
    }
  })

  eventsStore.on(
    e.UPLOAD_LOG_CONFIGURATION_UPDATED,
    (_, { uploadLogConfiguration: { item } }: ISubscription) => {
      if (item) {
        rootStore.uploadLogConfigurationStore.receiveUploadLogConfiguration(
          item,
        )
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_USERS, state => {
    return {
      graphSubscription: {
        query: ListenToUsersByProjectDocument,
        variables: { projectId: state.activeProject.id },
        onData: [e.USER_RECEIVED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_USERS],
      },
    }
  })

  eventsStore.on(
    e.USER_RECEIVED,
    (state, { user: { item } }: ISubscription) => {
      const user = User.fromDto(item)

      if (user.id === state.user.id) {
        state.user = user
      }

      if (rootStore.userProjectsStore.hasUserAccessToActiveProject(user)) {
        rootStore.projectMembersStore.setOne(user.getCopy() as User)
      }
    },
  )

  eventsStore.on(e.INIT_COMPLETE, state => {
    state.loading.clear()
    // Re-write INIT_APP=false after the clear so Routes.store's
    // bootstrap gate (loading.get(INIT_APP) !== false) stays released
    // on short-circuit exits — empty projects, invalid initProjectCode,
    // shouldInterruptInitialization, unauth flow, etc. (STX1-508).
    state.loading.set(e.INIT_APP, false)
    state.didInitialize = true
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_DELIVERY_ASSIGNMENTS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERY_ASSIGNMENTS, true)

    return {
      graphQuery: {
        query: GetDeliveryAssignmentListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.DELIVERY_ASSIGNMENTS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_DELIVERY_ASSIGNMENTS],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_ASSIGNMENTS_RECEIVED,
    (state, { deliveryAssignments }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERY_ASSIGNMENTS, false)
      rootStore.deliveryAssignmentsStore.receiveList(
        deliveryAssignments?.data || [],
      )
      return {
        dispatch: [e.LISTEN_TO_DELIVERY_ASSIGNMENTS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_DELIVERY_ASSIGNMENTS, state => {
    return {
      graphSubscription: {
        query: ListenToDeliveryAssignmentsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.DELIVERY_ASSIGNMENTS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_DELIVERY_ASSIGNMENTS],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_ASSIGNMENTS_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ deliveryAssignment }) => {
        if (deliveryAssignment) {
          rootStore.deliveryAssignmentsStore.receiveOne(
            deliveryAssignment.id,
            deliveryAssignment.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_ASSIGNMENTS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_ASSIGNMENTS, true)

    return {
      graphQuery: {
        query: GetSitePermitAssignmentListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SITE_PERMIT_ASSIGNMENTS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_SITE_PERMIT_ASSIGNMENTS,
        ],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_ASSIGNMENTS_RECEIVED,
    (state, { sitePermitAssignments }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_SITE_PERMIT_ASSIGNMENTS, false)
      rootStore.workflowAssignmentsStore.receiveList(
        sitePermitAssignments?.data || [],
      )
      return {
        dispatch: [e.LISTEN_TO_SITE_PERMIT_ASSIGNMENTS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_SITE_PERMIT_ASSIGNMENTS, state => {
    return {
      graphSubscription: {
        query: ListenToSitePermitAssignmentsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.SITE_PERMIT_ASSIGNMENTS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SITE_PERMIT_ASSIGNMENTS],
      },
    }
  })

  eventsStore.on(
    e.SITE_PERMIT_ASSIGNMENTS_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ sitePermitAssignment }) => {
        if (sitePermitAssignment) {
          rootStore.workflowAssignmentsStore.receiveOne(
            sitePermitAssignment.id,
            sitePermitAssignment.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_DELIVERIES_FOLLOWINGS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERIES_FOLLOWINGS, true)

    return {
      graphQuery: {
        query: GetDeliveryFollowingListDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.DELIVERY_FOLLOWINGS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_DELIVERIES_FOLLOWINGS],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_FOLLOWINGS_RECEIVED,
    (state, { deliveryFollowings }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERIES_FOLLOWINGS, false)

      rootStore.deliveryFollowingsStore.receiveList(
        (deliveryFollowings?.data as IAssociationDto[]) || [],
      )

      return {
        dispatch: [e.LISTEN_TO_DELIVERY_FOLLOWING],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_DELIVERY_FOLLOWING, state => {
    return {
      graphSubscription: {
        query: ListenToDeliveryFollowingDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.DELIVERY_FOLLOWING_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_DELIVERY_FOLLOWING],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_FOLLOWING_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ deliveryFollowing }) => {
        if (deliveryFollowing) {
          rootStore.deliveryFollowingsStore.receiveOne(
            deliveryFollowing.id,
            deliveryFollowing.item as IAssociationDto,
          )
        }
      })
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_TEAMS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TEAMS, true)

    return {
      graphQuery: {
        query: TeamsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_TEAMS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PROJECT_TEAMS],
      },
    }
  })

  eventsStore.on(e.PROJECT_TEAMS_RECEIVED, (state, { teams }: IQuery) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TEAMS, false)

    rootStore.projectTeamsStore.receiveList(teams?.data || [])

    return {
      dispatch: [e.LISTEN_TO_PROJECT_TEAMS],
    }
  })

  eventsStore.on(e.LISTEN_TO_PROJECT_TEAMS, state => {
    return {
      graphSubscription: {
        query: ListenTeamsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_TEAMS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_TEAMS],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_TEAMS_UPDATED,
    (_: InitialState, { team }: ISubscription) => {
      if (team) {
        rootStore.projectTeamsStore.receiveOne(team.id, team.item)
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_TRADES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TRADES, true)

    return {
      graphQuery: {
        query: TradesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_TRADES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PROJECT_TRADES],
      },
    }
  })

  eventsStore.on(e.PROJECT_TRADES_RECEIVED, (state, { trades }: IQuery) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TRADES, false)

    rootStore.projectTradesStore.receiveList(trades?.data || [])

    return {
      dispatch: [e.LISTEN_TO_PROJECT_TRADES],
    }
  })

  eventsStore.on(e.LISTEN_TO_PROJECT_TRADES, state => {
    return {
      graphSubscription: {
        query: ListenTradesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_TRADE_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_TRADES],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_TRADE_UPDATED,
    (_: InitialState, { trade }: ISubscription) => {
      if (trade) {
        rootStore.projectTradesStore.receiveOne(trade.id, trade.item)
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_ROLES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_ROLES, true)

    return {
      graphQuery: {
        query: UserRolesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_ROLES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PROJECT_ROLES],
      },
    }
  })

  eventsStore.on(e.PROJECT_ROLES_RECEIVED, (state, { userRoles }: IQuery) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_ROLES, false)

    rootStore.projectRolesStore.receiveList(userRoles?.data || [])

    return {
      dispatch: [e.LISTEN_TO_PROJECT_ROLES],
    }
  })

  eventsStore.on(e.LISTEN_TO_PROJECT_ROLES, state => {
    return {
      graphSubscription: {
        query: ListenUserRolesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_ROLES_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_ROLES],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_ROLES_UPDATED,
    (_: InitialState, { userRole }: ISubscription) => {
      if (userRole) {
        rootStore.projectRolesStore.receiveOne(userRole.id, userRole.item)
      }
    },
  )

  eventsStore.on(e.GET_PROJECT_CONFIG, state => {
    const { id: activeProjectId } = state.activeProject

    if (!activeProjectId) {
      return
    }

    return {
      graphQuery: {
        query: ProjectConfigurationByNameDocument,
        variables: {
          id: activeProjectId,
        },
        onSuccess: [e.PROJECT_CONFIG_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_PROJECT_CONFIG],
      },
    }
  })

  eventsStore.on(e.PROJECT_CONFIG_RECEIVED, (state, { project }: IQuery) => {
    state.featureFlags = {}
    flaggedFeaturesList.forEach(feature => {
      state.featureFlags[feature] = project.projectConfig[feature]
    })
    if (!state.isTilesetsDisabled) {
      state.tilesetsConfiguration = project.projectConfig.tilesetsConfiguration
    }

    if (!state.isWorkflowsDisabled) {
      state.flaggedFormCategories = {}
      flaggedFormCategoriesList.forEach(category => {
        // default to true if not defined
        const isEnabled = project.projectConfig.formCategories?.[category]
        state.flaggedFormCategories[category] = isEnabled ?? true
      })
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_MATERIAL_OPTIONS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIAL_OPTIONS, true)

    return {
      graphQuery: {
        query: GetProjectMaterialOptionsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.MATERIAL_OPTIONS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_MATERIAL_OPTIONS],
      },
    }
  })

  eventsStore.on(
    e.MATERIAL_OPTIONS_RECEIVED,
    (state, { projectMaterialOptions }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_MATERIAL_OPTIONS, false)

      state.projectMaterialOptions =
        projectMaterialOptions.data[0] ||
        state.getDefaultProjectMaterialOptions()

      return {
        dispatch: [e.LISTEN_TO_MATERIAL_OPTIONS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_MATERIAL_OPTIONS, state => {
    return {
      graphSubscription: {
        query: ListenToProjectMaterialOptionsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.MATERIAL_OPTIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_MATERIAL_OPTIONS],
      },
    }
  })

  eventsStore.on(
    e.MATERIAL_OPTIONS_UPDATED,
    (state: InitialState, { projectMaterialOptions }: ISubscription) => {
      state.projectMaterialOptions = projectMaterialOptions.item
    },
  )

  eventsStore.on(e.LISTEN_TO_PROJECT, state => {
    return {
      graphSubscription: {
        query: ListenToProjectDocument,
        variables: {
          id: state.activeProject.id,
        },
        onData: [e.PROJECT_RECEIVED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT],
      },
    }
  })

  eventsStore.on(e.PROJECT_RECEIVED, (state, { project }: ISubscription) => {
    const projectDto = project.item

    const areMaterialsUpdated =
      state.activeProject.materialsUploadId !== projectDto.materialsUploadId

    rootStore.projectsStore.setOne(projectDto)
    state.activeProject = Project.fromDto(projectDto)

    if (areMaterialsUpdated) {
      return {
        dispatch: [e.MATERIALS_UPLOADED],
      }
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_ADDRESS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_ADDRESS, true)
    return {
      graphQuery: {
        query: GetProjectAddressDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_ADDRESS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PROJECT_ADDRESS],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_ADDRESS_RECEIVED,
    (state, { projectAddresses }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_ADDRESS, false)
      state.projectAddress =
        projectAddresses.data[0] || state.getDefaultProjectAddress()

      return {
        dispatch: [e.LISTEN_TO_PROJECT_ADDRESS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_PROJECT_ADDRESS, state => {
    return {
      graphSubscription: {
        query: ListenToProjectAddressDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_ADDRESS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_ADDRESS],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_ADDRESS_UPDATED,
    (state: InitialState, { projectAddress }: ISubscription) => {
      state.loading.set(e.SAVE_PROJECT_ADDRESSES, false)
      state.projectAddress = projectAddress.item
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS, true)

    return {
      graphQuery: {
        query: GetProjectTypeOptionsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_TYPE_OPTIONS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_PROJECT_TYPE_OPTIONS, state => {
    return {
      graphSubscription: {
        query: ListenToProjectTypeOptionsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_TYPE_OPTIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_TYPE_OPTIONS],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_TYPE_OPTIONS_UPDATED,
    (state: InitialState, { projectTypeOptions }: ISubscription) => {
      const wasFormsDisabled = state.projectTypeOptions.isFormsDisabled
      const isFormsDisabledNow = projectTypeOptions.item.isFormsDisabled

      state.loading.set(e.SAVE_PROJECT_TYPE_OPTIONS, false)
      state.projectTypeOptions = projectTypeOptions.item

      if (wasFormsDisabled && !isFormsDisabledNow) {
        return {
          dispatch: [e.GET_WORKFLOW_CUSTOM_FILTERS],
        }
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_COLORING_OPTIONS, state => {
    return {
      graphQuery: {
        query: GetProjectColoringOptionsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_COLORING_OPTIONS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_PROJECT_COLORING_OPTIONS,
        ],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_COLORING_OPTIONS_RECEIVED,
    (state, { projectColoringOptions }: IQuery) => {
      state.projectColoringOptions =
        projectColoringOptions.data[0] ||
        state.getDefaultProjectColoringOptions()

      return {
        dispatch: [e.LISTEN_TO_PROJECT_COLORING_OPTIONS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_PROJECT_COLORING_OPTIONS, state => {
    return {
      graphSubscription: {
        query: ListenToProjectColoringOptionsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_COLORING_OPTIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_COLORING_OPTIONS],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_COLORING_OPTIONS_UPDATED,
    (state: InitialState, { projectColoringOptions }: ISubscription) => {
      state.projectColoringOptions = projectColoringOptions.item
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_DELIVERY_CONFIGURATIONS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERY_CONFIGURATIONS, true)

    return {
      graphQuery: {
        query: GetDeliveryConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.DELIVERY_CONFIGURATIONS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_DELIVERY_CONFIGURATIONS,
        ],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_CONFIGURATIONS_RECEIVED,
    (state, { deliveryConfigurations }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERY_CONFIGURATIONS, false)

      state.updateDeliveryConfigurations(deliveryConfigurations.data[0])

      return {
        dispatch: [e.LISTEN_TO_DELIVERY_CONFIGURATIONS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_DELIVERY_CONFIGURATIONS, state => {
    return {
      graphSubscription: {
        query: ListenToDeliveryConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.DELIVERY_CONFIGURATIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_DELIVERY_CONFIGURATIONS],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_CONFIGURATIONS_UPDATED,
    (state: InitialState, { deliveryConfigurations }: ISubscription) => {
      state.updateDeliveryConfigurations(deliveryConfigurations.item)
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_LOGISTICS_CONFIGURATIONS, state => {
    return {
      graphQuery: {
        query: GetLogisticsConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.LOGISTICS_CONFIGURATIONS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_LOGISTICS_CONFIGURATIONS,
        ],
      },
    }
  })

  eventsStore.on(
    e.LOGISTICS_CONFIGURATIONS_RECEIVED,
    (state, { logisticsConfigurations }: IQuery) => {
      state.loading.set(e.GET_LOGISTICS_CONFIG_AND_OPEN_DEFAULT_PAGE, false)
      state.updateLogisticsConfigurations(logisticsConfigurations.data[0])

      return {
        dispatch: [e.LISTEN_TO_LOGISTICS_CONFIGURATIONS],
      }
    },
  )

  eventsStore.on(
    e.LOGISTICS_CONFIGURATIONS_UPDATED,
    (state: InitialState, { logisticsConfigurations }: ISubscription) => {
      state.updateLogisticsConfigurations(logisticsConfigurations.item)
    },
  )

  eventsStore.on(e.LISTEN_TO_LOGISTICS_CONFIGURATIONS, state => {
    return {
      graphSubscription: {
        query: ListenToLogisticsConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.LOGISTICS_CONFIGURATIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_LOGISTICS_CONFIGURATIONS],
      },
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_WORKFLOWS_CONFIGURATIONS, state => {
    return {
      graphQuery: {
        query: GetWorkflowsConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.WORKFLOWS_CONFIGURATIONS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_WORKFLOWS_CONFIGURATIONS,
        ],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOWS_CONFIGURATIONS_RECEIVED,
    (state, { workflowConfigurations }: IQuery) => {
      state.updateWorkflowsConfigurations(workflowConfigurations.data[0])

      return {
        dispatch: [e.LISTEN_TO_WORKFLOWS_CONFIGURATIONS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_WORKFLOWS_CONFIGURATIONS, state => {
    return {
      graphSubscription: {
        query: ListenToWorkflowsConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.WORKFLOWS_CONFIGURATIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_WORKFLOWS_CONFIGURATIONS],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOWS_CONFIGURATIONS_UPDATED,
    (state: InitialState, { workflowConfigurations }: ISubscription) => {
      state.updateWorkflowsConfigurations(workflowConfigurations.item)
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS, state => {
    if (!state.delivery.isDefaultsReceived || !state.activeProject.id) {
      return
    }

    state.loading.set(e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS, true)

    return {
      graphQuery: {
        query: GetDeliveryFieldsConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.DELIVERY_FIELDS_CONFIGURATIONS_RECEIVED],
        onError: [
          e.REQUEST_ERROR,
          e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS,
        ],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_FIELDS_CONFIGURATIONS_RECEIVED,
    (state, { deliveryFieldsConfigurations }: IQuery) => {
      state.loading.set(
        e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS,
        false,
      )

      const custom = deliveryFieldsConfigurations.data[0]
      const { defaultMandatoryFields, defaultHiddenFields } = state.delivery

      state.delivery.mandatoryFields =
        (custom?.mandatoryFields && listToMap(custom.mandatoryFields)) ||
        defaultMandatoryFields

      state.delivery.hiddenFields =
        (custom?.hiddenFields && listToMap(custom.hiddenFields)) ||
        defaultHiddenFields

      state.delivery.customFieldNames = custom?.fieldNames
        ? listToMap(
            custom.fieldNames,
            item => item.id,
            item => item.name,
          )
        : {}

      state.delivery.deliveryFieldsConfigId = custom?.id

      return {
        dispatch: [e.LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS, state => {
    return {
      graphSubscription: {
        query: ListenToDeliveryFieldsConfigurationsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.DELIVERY_FIELDS_CONFIGURATIONS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS],
      },
    }
  })

  eventsStore.on(
    e.DELIVERY_FIELDS_CONFIGURATIONS_UPDATED,
    (state: InitialState, { deliveryFieldsConfigurations }: ISubscription) => {
      const { mandatoryFields, hiddenFields, fieldNames, id } =
        deliveryFieldsConfigurations.item

      state.delivery.deliveryFieldsConfigId = id
      state.delivery.mandatoryFields = listToMap(mandatoryFields)
      state.delivery.hiddenFields = listToMap(hiddenFields)
      state.delivery.customFieldNames = listToMap(
        fieldNames,
        item => item.id,
        item => item.name,
      )
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_ZONEMAP_THRESHOLDS, state => {
    return {
      graphQuery: {
        query: GetZoneMapThresholdsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.ZONEMAP_THRESHOLDS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_ZONEMAP_THRESHOLDS],
      },
    }
  })

  eventsStore.on(
    e.ZONEMAP_THRESHOLDS_RECEIVED,
    (state, { zoneMapThresholds }: IQuery) => {
      state.zoneMapThresholds =
        zoneMapThresholds.data[0] || state.getDefaultZoneMapThresholds()

      return {
        dispatch: [e.LISTEN_TO_ZONEMAP_THRESHOLDS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_ZONEMAP_THRESHOLDS, state => {
    return {
      graphSubscription: {
        query: ListenToZoneMapThresholdsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.ZONEMAP_THRESHOLDS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_ZONEMAP_THRESHOLDS],
      },
    }
  })

  eventsStore.on(
    e.ZONEMAP_THRESHOLDS_UPDATED,
    (state: InitialState, { zoneMapThresholds }: ISubscription) => {
      state.zoneMapThresholds = zoneMapThresholds.item
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY, true)

    return {
      graphQuery: {
        query: GetProjectHistoryDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PROJECT_HISTORY_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_HISTORY_RECEIVED,
    (state, { projectHistories }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY, false)
      state.projectHistory =
        projectHistories.data[0] || state.getDefaultProjectHistory()

      return {
        dispatch: [e.LISTEN_TO_PROJECT_HISTORY],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_PROJECT_HISTORY, state => {
    return {
      graphSubscription: {
        query: ListenToProjectHistoryDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PROJECT_HISTORY_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PROJECT_HISTORY],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_HISTORY_UPDATED,
    (state: InitialState, { projectHistory }: ISubscription) => {
      state.loading.set(e.SAVE_PROJECT_HISTORY, false)
      state.projectHistory = projectHistory.item
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_COMPANIES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_COMPANIES, true)

    return {
      graphQuery: {
        query: CompaniesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.COMPANIES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_COMPANIES],
      },
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SCANNERS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SCANNERS, true)

    return {
      graphQuery: {
        query: ScannersByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SCANNERS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_SCANNERS],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_SCANNERS, state => {
    return {
      graphSubscription: {
        query: ListenScannersByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.SCANNER_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SCANNERS],
      },
    }
  })

  eventsStore.on(e.SCANNERS_RECEIVED, (state, { scanners }) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SCANNERS, false)

    rootStore.scannersStore.receiveList(scanners ? scanners.data : [])

    return {
      dispatch: [e.LISTEN_TO_SCANNERS],
    }
  })

  eventsStore.on(
    e.SCANNER_UPDATED,
    (_: InitialState, { scanner }: ISubscription) => {
      if (scanner) {
        rootStore.scannersStore.receiveOne(scanner.id, scanner.item)
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_SCAN_HISTORIES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SCAN_HISTORIES, true)

    return {
      graphQuery: {
        query: ScanHistoriesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.SCAN_HISTORIES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_SCAN_HISTORIES],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_SCAN_HISTORIES, state => {
    return {
      graphSubscription: {
        query: ListenScanHistoriesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.SCAN_HISTORY_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_SCAN_HISTORIES],
      },
    }
  })

  eventsStore.on(e.SCAN_HISTORIES_RECEIVED, (state, { scanHistories }) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_SCAN_HISTORIES, false)

    rootStore.scanHistoriesStore.receiveList(scanHistories?.data || [])

    return {
      dispatch: [e.LISTEN_TO_SCAN_HISTORIES],
    }
  })

  eventsStore.on(
    e.SCAN_HISTORY_UPDATED,
    (_: InitialState, { scanHistory }: ISubscription) => {
      if (scanHistory) {
        rootStore.scanHistoriesStore.receiveOne(
          scanHistory.id,
          scanHistory.item,
        )
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_COMPANIES, state => {
    return {
      graphSubscription: {
        query: ListenCompaniesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.COMPANIES_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_COMPANIES],
      },
    }
  })

  eventsStore.on(e.COMPANIES_RECEIVED, (state, { companies }) => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_COMPANIES, false)

    rootStore.companiesStore.receiveList(companies ? companies.data : [])

    return {
      dispatch: [e.LISTEN_TO_COMPANIES],
    }
  })

  eventsStore.on(
    e.COMPANIES_UPDATED,
    (_: InitialState, { company }: ISubscription) => {
      if (company) {
        rootStore.companiesStore.receiveOne(company.id, company.item)
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_USER_PROJECTS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_USER_PROJECTS, true)

    return {
      graphQuery: {
        query: UserProjectsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.USER_PROJECTS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_USER_PROJECTS],
      },
    }
  })

  eventsStore.on(e.LISTEN_TO_USER_PROJECTS, state => {
    return {
      graphSubscription: {
        query: ListenUserProjectsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.USER_PROJECT_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_USER_PROJECTS],
      },
    }
  })

  eventsStore.on(
    e.USER_PROJECTS_RECEIVED,
    (state, { userProjects }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_USER_PROJECTS, false)

      rootStore.userProjectsStore.receiveList(
        userProjects ? userProjects.data : [],
      )

      return {
        dispatch: [e.LISTEN_TO_USER_PROJECTS],
      }
    },
  )

  eventsStore.on(
    e.USER_PROJECT_UPDATED,
    (state: InitialState, { userProject: { item } }: ISubscription) => {
      if (!item) {
        return
      }

      const { userId, isDeleted } = item
      rootStore.userProjectsStore.receiveOne(userId, item)

      const isAuthUserUpdated = userId === state.user.id

      if (isAuthUserUpdated) {
        if (isDeleted) {
          safeSetItem(state.user.id, JSON.stringify({ lastViewedProject: '' }))

          return {
            dispatch: [e.INIT_APP],
          }
        } else {
          state.userActiveProjectSettings = UserProject.fromDto(item)
        }
      }
    },
  )

  eventsStore.on(
    e.GET_CONFIGURATION,
    (state: InitialState, name: string, onSuccessAction) => {
      state.loading.set(`${e.GET_CONFIGURATION}_${name}`, true)

      return {
        graphQuery: {
          query: ConfigurationByNameDocument,
          variables: { name },
          onSuccess: [onSuccessAction],
          onError: [e.REQUEST_ERROR, e.GET_CONFIGURATION],
        },
      }
    },
  )

  eventsStore.on(
    e.DEFAULT_HIDDEN_FIELDS_CONFIGURATION_RECEIVED,
    (state: InitialState, { configuration }) => {
      const cName = ConfigurationName.DefaultHiddenFields
      state.loading.set(`${e.GET_CONFIGURATION}_${cName}`, false)
      try {
        state.delivery.defaultHiddenFields = JSON.parse(configuration?.value)

        if (
          state.delivery.defaultMandatoryFields &&
          !state.delivery.isDefaultsReceived
        ) {
          state.delivery.isDefaultsReceived = true
          return {
            dispatch: [e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS],
          }
        }
      } catch {
        console.warn(`Incorrect configuration [${cName}]`)
      }
    },
  )

  eventsStore.on(
    e.DEFAULT_MANDATORY_FIELDS_CONFIGURATION_RECEIVED,
    (state: InitialState, { configuration }) => {
      const cName = ConfigurationName.DefaultMandatoryFields
      state.loading.set(`${e.GET_CONFIGURATION}_${cName}`, false)
      try {
        state.delivery.defaultMandatoryFields = JSON.parse(configuration?.value)
        if (
          state.delivery.defaultHiddenFields &&
          !state.delivery.isDefaultsReceived
        ) {
          state.delivery.isDefaultsReceived = true
          return {
            dispatch: [e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS],
          }
        }
      } catch {
        console.warn(`Incorrect configuration [${cName}]`)
      }
    },
  )

  eventsStore.on(e.LOAD_AND_LISTEN_TO_OPERATION_RULES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_OPERATION_RULES, true)

    return {
      graphQuery: {
        query: OperationRulesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.OPERATION_RULES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_OPERATION_RULES],
      },
    }
  })

  eventsStore.on(
    e.OPERATION_RULES_RECEIVED,
    (state, { operationRules }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_OPERATION_RULES, false)

      rootStore.operationRulesStore.receiveList(operationRules?.data || [])

      return {
        dispatch: [e.LISTEN_TO_OPERATION_RULES],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_OPERATION_RULES, state => {
    return {
      graphSubscription: {
        query: ListenOperationRuleByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.OPERATION_RULES_UPDATED],
        listenMany: true,
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_OPERATION_RULES],
      },
    }
  })

  eventsStore.on(
    e.OPERATION_RULES_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ operationRule }) => {
        if (operationRule) {
          rootStore.operationRulesStore.receiveOne(
            operationRule.id,
            operationRule.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.GET_DEFAULT_TEAMS, (state: InitialState) => {
    state.loading.set(e.GET_DEFAULT_TEAMS, true)

    return {
      graphQuery: {
        query: DefaultTeamsDocument,
        variables: { limit: 100000 },
        onSuccess: [e.DEFAULT_TEAMS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_DEFAULT_TEAMS],
      },
    }
  })

  eventsStore.on(
    e.DEFAULT_TEAMS_RECEIVED,
    (state: InitialState, { defaultTeams }: IQuery) => {
      state.loading.set(e.GET_DEFAULT_TEAMS, false)

      rootStore.projectDefaultTeamsStore.receiveList(defaultTeams?.data)
    },
  )

  eventsStore.on(e.GET_DEFAULT_WORKFLOW_TYPES, (state: InitialState) => {
    state.loading.set(e.GET_DEFAULT_WORKFLOW_TYPES, true)

    return {
      graphQuery: {
        query: GetDefaultPermitTypesDocument,
        variables: { limit: 100000 },
        onSuccess: [e.DEFAULT_WORKFLOW_TYPES_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_DEFAULT_WORKFLOW_TYPES],
      },
    }
  })

  eventsStore.on(
    e.DEFAULT_WORKFLOW_TYPES_RECEIVED,
    (state: InitialState, { defaultPermitTypes }: IQuery) => {
      state.loading.set(e.GET_DEFAULT_WORKFLOW_TYPES, false)

      rootStore.workflowTypesStore.receiveDefaultTypes(defaultPermitTypes?.data)
    },
  )

  eventsStore.on(
    e.GET_PDF_BLOB_URL,
    (state: InitialState, fileUrl: string, callbackFn?) => {
      state.loading.set(e.GET_PDF_BLOB_URL, true)

      return {
        http: {
          url: fileUrl,
          method: 'GET',
          mode: 'no-cors',
          blob: true,
          onSuccess: [e.GET_PDF_BLOB_URL_SUCCESS, callbackFn],
          onError: [e.REPORT_ERROR, e.GET_PDF_BLOB_URL],
        },
      }
    },
  )

  eventsStore.on(
    e.GET_PDF_BLOB_URL_SUCCESS,
    (state: InitialState, callbackFn?, blob?: Blob) => {
      state.loading.set(e.GET_PDF_BLOB_URL, false)

      return {
        dispatch: [e.RUN_CALLBACK, callbackFn(blob)],
      }
    },
  )

  eventsStore.on(
    e.GET_WEATHER_FORECASTS,
    (state, startDate: number, endDate: number) => {
      state.loading.set(e.GET_WEATHER_FORECASTS, true)

      return {
        graphQuery: {
          query: GetWeatherForecastsRangeDocument,
          variables: {
            forecastRangeRequest: {
              projectId: state.activeProject.id,
              startDate,
              endDate,
              timezoneId: rootStore.projectDateStore.getClientTimezoneId(),
            },
          },
          onSuccess: [e.WEATHER_FORECASTS_RECEIVED],
          onError: [e.REQUEST_ERROR, e.GET_WEATHER_FORECASTS],
        },
      }
    },
  )

  eventsStore.on(
    e.WEATHER_FORECASTS_RECEIVED,
    (state, { weatherForecasts }: IQuery) => {
      state.loading.set(e.GET_WEATHER_FORECASTS, false)

      rootStore.weatherForecastsStore.receiveWeather(weatherForecasts)

      return {
        dispatch: [e.NO_EFFECT],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_WEATHER_FORECASTS, state => {
    return {
      graphSubscription: {
        query: ListenWeatherForecastsByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.WEATHER_FORECAST_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_WEATHER_FORECASTS],
      },
    }
  })

  eventsStore.on(
    e.WEATHER_FORECAST_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ weatherForecast }) => {
        if (weatherForecast) {
          rootStore.weatherForecastsStore.receiveOne(weatherForecast.item)
        }
      })
    },
  )

  eventsStore.on(e.GET_RECURRING_DELIVERIES_SETTING, (state, id: string) => {
    state.loading.set(e.GET_RECURRING_DELIVERIES_SETTING, true)

    return {
      graphQuery: {
        query: GetRecurringDeliveriesSettingsByIdDocument,
        variables: {
          id,
        },
        onSuccess: [e.RECURRING_DELIVERIES_SETTING_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_RECURRING_DELIVERIES_SETTING],
      },
    }
  })

  eventsStore.on(
    e.RECURRING_DELIVERIES_SETTING_RECEIVED,
    (state, { recurringDeliveriesSettings }: IQuery) => {
      state.loading.set(e.GET_RECURRING_DELIVERIES_SETTING, false)

      rootStore.recurringDeliveriesSettingsStore.receiveOne(
        recurringDeliveriesSettings?.id,
        recurringDeliveriesSettings,
      )

      return {
        dispatch: [e.NO_EFFECT],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_RECURRING_DELIVERIES_SETTING, state => {
    return {
      graphSubscription: {
        query: ListenToRecurringDeliveriesSettingsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.RECURRING_DELIVERIES_SETTING_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_RECURRING_DELIVERIES_SETTING],
      },
    }
  })

  eventsStore.on(
    e.RECURRING_DELIVERIES_SETTING_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ recurringDeliveriesSettings }) => {
        if (recurringDeliveriesSettings) {
          rootStore.recurringDeliveriesSettingsStore.receiveOne(
            recurringDeliveriesSettings.id,
            recurringDeliveriesSettings.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.GET_RECURRING_WORKFLOW_SETTINGS, (state, id: string) => {
    state.loading.set(e.GET_RECURRING_WORKFLOW_SETTINGS, true)

    return {
      graphQuery: {
        query: GetRecurringWorkflowSettingsByIdDocument,
        variables: {
          id,
        },
        onSuccess: [e.RECURRING_WORKFLOW_SETTINGS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_RECURRING_WORKFLOW_SETTINGS],
      },
    }
  })

  eventsStore.on(
    e.RECURRING_WORKFLOW_SETTINGS_RECEIVED,
    (state, { recurringWorkflowSettings }: IQuery) => {
      state.loading.set(e.GET_RECURRING_WORKFLOW_SETTINGS, false)

      rootStore.recurringWorkflowSettingsStore.receiveOne(
        recurringWorkflowSettings?.id,
        recurringWorkflowSettings,
      )

      return {
        dispatch: [e.NO_EFFECT],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_RECURRING_WORKFLOW_SETTINGS, state => {
    return {
      graphSubscription: {
        query: ListenToRecurringWorkflowSettingsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        listenMany: true,
        onData: [e.RECURRING_WORKFLOW_SETTINGS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_RECURRING_WORKFLOW_SETTINGS],
      },
    }
  })

  eventsStore.on(
    e.RECURRING_WORKFLOW_SETTINGS_UPDATED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ recurringWorkflowSettings }) => {
        if (recurringWorkflowSettings) {
          rootStore.recurringWorkflowSettingsStore.receiveOne(
            recurringWorkflowSettings.id,
            recurringWorkflowSettings.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.LOAD_REPORT_TEMPLATE, (_, templateId: string, callback) => {
    return {
      graphQuery: {
        query: GetReportTemplateDocument,
        variables: {
          templateId,
        },
        onSuccess: [e.REPORT_TEMPLATE_RECEIVED, callback],
        onError: [e.RUN_CALLBACK, callback],
      },
    }
  })

  eventsStore.on(
    e.REPORT_TEMPLATE_RECEIVED,
    (_, callback, { reportTemplate }: IQuery) => {
      return {
        dispatch: [e.RUN_CALLBACK, callback, reportTemplate],
      }
    },
  )

  eventsStore.on(
    e.PROJECT_INTEGRATIONS_CONFIGS_RECEIVED,
    (state: InitialState, { configuration }) => {
      const cName = getIntegrationsConfigName(state.activeProject.id)
      state.loading.set(`${e.GET_CONFIGURATION}_${cName}`, false)

      state.clearIntegrations()

      try {
        // eslint-disable-next-line no-var
        var configs = JSON.parse(configuration?.value || '[]')
      } catch {
        console.error('Unable to parse integration configurations')
        return
      }

      configs.forEach(config => state.receiveIntegrationConfig(config))

      return {
        dispatch: [e.INIT_PROJECT_INTEGRATIONS],
      }
    },
  )

  eventsStore.on(e.MATERIALS_UPLOADED, () => {
    rootStore.materialCategoryStore.clearList()
    rootStore.materialsStore.clearList()

    return {
      dispatchN: [
        [e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES],
        [e.LOAD_AND_LISTEN_TO_MATERIALS],
        [e.RESET_MATERIALS_FILTERS],
      ],
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES, true)

    return {
      graphQuery: {
        query: GetFormCategoriesDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.WORKFLOW_CATEGORIES_LOAD_SUCCESS],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOW_CATEGORIES_LOAD_SUCCESS,
    (state: InitialState, { formCategories }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES, false)

      rootStore.workflowCategoriesStore.receiveList(formCategories.data)

      return {
        dispatch: [e.LISTEN_TO_WORKFLOW_CATEGORIES],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_WORKFLOW_CATEGORIES, state => {
    return {
      graphSubscription: {
        query: ListenFormCategoriesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.WORKFLOW_CATEGORIES_RECEIVED],
        listenMany: true,
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_WORKFLOW_CATEGORIES],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOW_CATEGORIES_RECEIVED,
    (_: InitialState, data: ISubscription[]) => {
      data.forEach(({ formCategory }) => {
        if (formCategory) {
          rootStore.workflowCategoriesStore.receiveOne(
            formCategory.id,
            formCategory.item,
          )
        }
      })
    },
  )

  eventsStore.on(e.LISTEN_TO_WORKFLOW_LOCATION_RESTRICTION, state => {
    const requestedProjectId = state.activeProject.id
    const generation = ++workflowRestrictionGeneration
    return {
      graphSubscription: {
        query: ListenToWorkflowLocationRestrictionDocument,
        variables: {
          projectId: requestedProjectId,
        },
        onData: [
          e.WORKFLOW_LOCATION_RESTRICTION_RECEIVED,
          requestedProjectId,
          generation,
        ],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_WORKFLOW_LOCATION_RESTRICTION],
      },
    }
  })

  eventsStore.on(
    e.WORKFLOW_LOCATION_RESTRICTION_RECEIVED,
    (
      state,
      requestedProjectId: string,
      generation: number,
      { workflowLocationRestriction }: ISubscription,
    ) => {
      if (
        state.activeProject.id !== requestedProjectId ||
        generation !== workflowRestrictionGeneration ||
        !workflowLocationRestriction
      ) {
        return
      }
      const id =
        workflowLocationRestriction.id ?? workflowLocationRestriction.item?.id
      if (id === null || id === undefined) return

      const delta = {
        id: String(id),
        item: workflowLocationRestriction.item ?? null,
      }
      rootStore.equipmentResourcesStore.receiveWorkflowRestriction(
        requestedProjectId,
        delta,
      )
      rootStore.materialResourcesStore.receiveWorkflowRestriction(
        requestedProjectId,
        delta,
      )
    },
  )

  /**
   * General purpose empty event to prevent creating same events
   */
  eventsStore.on(e.NO_EFFECT, () => {
    return
  })

  /**
   * General purpose error handling. To be expanded upon.
   */
  eventsStore.on(e.REQUEST_ERROR, handleRequestError)

  /**
   * Cleanup loading flag after a request is done
   */
  eventsStore.on(e.COMPLETE_REQUEST, handleCompleteRequest)

  /**
   * Report error to logging service
   */
  eventsStore.on(e.REPORT_ERROR, handleReportError)

  eventsStore.on(
    e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
    (state, eventType, callback, data) => {
      state.loading.set(eventType, false)
      callback?.(data)
    },
  )

  eventsStore.on(
    e.COMPLETE_REQUEST_AND_DISPATCH,
    (state, eventType, eventTypeToDispatch, ...params) => {
      state.loading.set(eventType, false)

      return {
        dispatch: [eventTypeToDispatch, ...params],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_DEPLOYMENT_VERSION, () => {
    if (isRemote) {
      return {
        http: {
          // Cache-bust the URL so the browser never sends a conditional GET
          // and we always get a fresh 200 with body. Without this, a cached
          // /.commit_id served back as 304 leaves response.text() empty and
          // the version compare below mis-fires into window.location.reload(),
          // producing an infinite reload loop (e.g. on the SSO callback page
          // before any post-auth navigation can complete).
          url: `/.commit_id?t=${Date.now()}`,
          method: 'GET',
          onSuccess: [e.DEPLOYMENT_VERSION_RECEIVED],
          onError: [e.LISTEN_TO_DEPLOYMENT_VERSION_ERROR],
        },
      }
    }
  })

  eventsStore.on(
    e.DEPLOYMENT_VERSION_RECEIVED,
    (_, deploymentVersion: string) => {
      window.RT = null

      // Reload at most once per unique mismatch pair per session. Without
      // this circuit breaker a preview env where the served bundle and
      // /.commit_id are stuck at different commits (deploy race / CDN lag)
      // loops the page indefinitely: bundle boot -> fetch -> mismatch ->
      // reload -> bundle boot -> same mismatch -> reload. After one attempt
      // we stay on whichever build we've got rather than thrashing.
      // Falsy deploymentVersion (transient server hiccup) is also skipped.
      const VERSION_RELOAD_KEY = 'stx-version-reload-attempted'
      if (
        deploymentVersion &&
        deploymentVersion !== Config.DEPLOYMENT_VERSION
      ) {
        // Don't reload while an auth callback is mid-flight. Pages like
        // /sso-callback, /reset-password, /save-password process one-time
        // URL params (sso_code, oobCode) that have already been stripped
        // from the URL via replaceState by the time this handler fires; a
        // reload here would drop the in-flight session and leave the user
        // on a permanent spinner. Defer to the next 60s tick by which time
        // the callback has navigated away.
        if (isTenantBootInterceptPath(location.pathname)) {
          console.warn(
            `Deployment version mismatch detected (${Config.DEPLOYMENT_VERSION} -> ${deploymentVersion}) ` +
              `but '${location.pathname}' is an auth callback in flight; deferring reload.`,
          )
        } else {
          const pair = `${Config.DEPLOYMENT_VERSION}->${deploymentVersion}`
          const lastAttempt = safeGetItem(VERSION_RELOAD_KEY, 'sessionStorage')
          if (lastAttempt !== pair) {
            // A denied write means the breaker cannot persist, so a genuine
            // version mismatch would reload forever. Reload only if the
            // guard actually stuck.
            if (safeSetItem(VERSION_RELOAD_KEY, pair, 'sessionStorage')) {
              window.location.reload()
              return
            }
            console.warn(
              `Deployment version mismatch (${pair}) but sessionStorage is ` +
                'unavailable to guard against a reload loop; staying on ' +
                'current bundle.',
            )
          } else {
            console.warn(
              `Deployment version mismatch persisted across reload (${pair}); ` +
                'staying on current bundle. Likely a preview env deploy race.',
            )
          }
        }
      } else if (deploymentVersion === Config.DEPLOYMENT_VERSION) {
        // Versions match — clear the breaker so a future legit deploy can
        // trigger one reload.
        safeRemoveItem(VERSION_RELOAD_KEY, 'sessionStorage')
      }

      const minuteMS = 60 * 1000

      window.RT = window.setTimeout(() => {
        eventsStore.dispatch(e.LISTEN_TO_DEPLOYMENT_VERSION)
      }, minuteMS)
    },
  )

  eventsStore.on(e.LISTEN_TO_DEPLOYMENT_VERSION_ERROR, () => {
    window.RT = null
  })

  // STX1-1057: open the set-password prompt for a user whose only possible
  // credential is a password. The INIT_APP_3 gate decides whether to dispatch
  // this; never raise it over the login screen.
  eventsStore.on(e.DISPLAY_SAVE_PASSWORD, state => {
    if (rootStore.common.history.location.pathname !== commonRoutes.LOGIN) {
      state.isSavePasswordPromptOpen = true
    }
  })

  // Password saved — close the prompt and leave the user where they were.
  eventsStore.on(e.SAVE_PASSWORD_SUCCESS, state => {
    state.isSavePasswordPromptOpen = false

    // The refresh of auth.hasPassword is dispatched from SET_PASSWORD_RESULT and
    // is async; INIT_APP_3 reruns on every project switch and would reopen the
    // prompt before it lands. Settle it for this session too.
    state.isSavePasswordPromptDismissed = true
  })

  registerCommonChangeEvents(eventsStore, rootStore)
  registerLiveDataResyncEvents(eventsStore, rootStore)
}

function shouldInterruptInitialization({
  state,
  projectsStore,
}: RootStore): boolean {
  return (
    !!state.initProjectCode && !projectsStore.hasActiveUserAccessToTargetProject
  )
}

function setLatestDeliveryByProjectAndUser({
  deliveriesStore,
  deliveryDetailsStore,
  state,
}: RootStore) {
  deliveryDetailsStore.latestDeliveryByProjectAndUser =
    deliveriesStore.getLatestDeliveryByProjectAndUser(
      state.activeProject.id,
      state.user.id,
    )
}

function updateDisplayedDeliveryIfNeed(
  { deliveriesStore, deliveryDetailsStore }: RootStore,
  updatedDeliveryId: string,
) {
  const { displayedDelivery } = deliveryDetailsStore
  if (displayedDelivery && displayedDelivery.id === updatedDeliveryId) {
    const updatedDelivery = deliveriesStore.byId.get(displayedDelivery.id)
    deliveryDetailsStore.displayedDelivery = updatedDelivery
    deliveryDetailsStore.prepareView()
  }
}

export function handleEventCallback(
  event: string,
  shouldCompleteRequest: boolean,
  callback: () => void,
): IEvent {
  return shouldCompleteRequest
    ? [e.COMPLETE_REQUEST_AND_RUN_CALLBACK, event, callback]
    : [e.RUN_CALLBACK, callback]
}

interface ITelemetryPayload {
  event?: {
    eventCategory: string
    eventAction: string
    eventLabel?: string
    eventValue?: number
  }
  timing?: {
    timingCategory: string
    timingVar: string
    timingValue: number
    timingLabel?: string
  }
}
